import { getOrderVendorIDs, getEffectiveOrderVendorID, getOrderSellerConversationKey, getCourierConversationKey } from "@/lib/order-chat-threads";
import { OrderChannelPanel } from "@/components/chat/order-channel-panel";
import { AdminFloatingSupport } from "@/components/chat/admin-floating-support";
import { useChatAttachments, validateChatFiles } from "@/hooks/use-chat-attachments";
import { ChatAttachmentPicker } from "@/components/chat/chat-attachment-picker";
import { ShopMessageAttachment } from "@/components/chat/shop-message-attachment";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import autopartsApi, {
  AUTH_SESSION_UPDATED_EVENT,
  AutopartsApiError,
  readStoredAuthSession,
  resolveUserAvatarUrl,
  type ApiUploadProgress,
  type AuthenticatedUser,
  type ConversationMediaAttachmentView,
  type ConversationMessageContextView,
  type ConversationMessageReplyView,
  type ConversationProductAttachmentView,
  type OrderMessageView,
  type OrderView,
  type PageResult,
  type ProductView,
  type ShopConversationMessageResult,
  type ShopConversationView,
  type VendorProfileView,
} from "@/api/autoparts";
import { ConversationMessageContext } from "@/components/conversation-message-context";
import { selectPrimaryProductImage } from "@/lib/product-images";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  buildSellerShopNotificationHref,
  getMessageProductContextId,
  getQualityAssessorRequestNotificationTitle,
  markMessageNotificationThreadRead,
  messageMatchesProductContext,
  MESSAGE_NOTIFICATION_NAVIGATE_EVENT,
  notifyNewIncomingMessages,
  type MessageNotificationNavigationDetail,
} from "@/lib/message-notifications";
import {
  disablePushNotifications,
  enablePushNotifications,
  getPushNotificationState,
  type PushNotificationState,
} from "@/lib/report-push-notifications";
import {
  ArrowLeft,
  Bell,
  BellRing,
  Download,
  FileImage,
  Film,
  ImagePlus,
  Check,
  ChevronDown,
  Loader2,
  MessageCircle,
  PackageCheck,
  PackagePlus,
  RefreshCw,
  Reply,
  Search,
  Send,
  X,
} from "@/components/icons";
import { toast } from "sonner";
import {
  countUnreadOrderMessages,
  filterCourierSellerOrderMessages,
  markOrderMessagesRead,
  ORDER_MESSAGE_READ_STATE_EVENT,
  resolveOrderMessageReadAt,
  type OrderMessageReaderRole,
} from "@/lib/order-message-read-state";
import { useDraggableFloating } from "@/hooks/use-draggable-floating";
import {
  FLOATING_LAUNCHER_CLASS,
  FLOATING_LAUNCHER_ICON_CLASS,
  FLOATING_LAUNCHER_STACK,
} from "@/components/floating-launcher";

type MessageRole = "buyer" | "seller" | "quality_assessor";

type FloatingMessageProps = {
  pathname: string;
  search: string;
};

export type FloatingMessageOpenDetail = {
  channel?: "shop" | "courier";
  orderId?: string;
  buyerId?: string;
  vendorId?: string;
  conversationKey?: string;
  productId?: string;
};

export const FLOATING_MESSAGE_OPEN_EVENT = "sparelynk:floating-message-open";

function getLastItem<T>(items: readonly T[] | null | undefined): T | undefined {
  return items?.[items.length - 1];
}

type ConversationKind = "shop" | "courier";

type BuyerConversationTarget = {
  vendorId: string;
  sellerName: string;
  sellerAvatarUrl?: string;
  context?: ConversationMessageContextView;
};

type PendingMessage = {
  buyerId: string;
  vendorId: string;
  message: OrderMessageView;
};

const SUPPORT_VENDOR_ID = "sparelynk-admin-support";

const MESSAGE_RECONCILIATION_ATTEMPTS = 2;
const MESSAGE_RECONCILIATION_DELAY_MS = 350;
const ATTACHMENT_CATALOG_PAGE_SIZE = 200;
const ATTACHMENT_CATALOG_CACHE_TTL_MS = 120_000;
const MESSAGE_RECONCILIATION_REQUEST_TIMEOUT_MS = 2_500;
const MESSAGE_MATCH_WINDOW_MS = 60_000;
const MAX_SELECTED_PRODUCTS = 12;
const MAX_SELECTED_ORDERS = 12;
const ORDER_MESSAGE_DRAFT_MAX_LENGTH = 1200;
const SWIPE_DRAG_THRESHOLD_PX = 6;
const SWIPE_REPLY_THRESHOLD_PX = 56;
const SWIPE_REPLY_MAX_OFFSET_PX = 84;
type SwipeReplyGesture = {
  messageId: string;
  pointerId: number;
  startX: number;
  isOwnMessage: boolean;
  dragging: boolean;
};

type SwipeReplyOffset = {
  messageId: string;
  offsetX: number;
};

type AttachmentCatalogCacheEntry<T> = {
  expiresAt: number;
  items: T[];
};

type OrderAttachmentCatalogCacheEntry =
  AttachmentCatalogCacheEntry<OrderView> & {
    vendorAliases: string[];
  };

type CourierSellerConversationGroup = {
  key: string;
  orders: OrderView[];
  order: OrderView;
  messages: OrderMessageView[];
  lastMessage?: OrderMessageView;
  unseenCount: number;
  sellerName: string;
  sellerAvatarUrl?: string;
};

type SellerCourierConversationGroup = {
  key: string;
  orders: OrderView[];
  order: OrderView;
  messages: OrderMessageView[];
  lastMessage?: OrderMessageView;
  unseenCount: number;
  courierName: string;
  courierAvatarUrl?: string;
};

function waitForMessageReconciliation(delayMs: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, delayMs));
}

function messageContextsMatch(
  persisted?: ConversationMessageContextView,
  optimistic?: ConversationMessageContextView,
): boolean {
  if (!optimistic) return true;
  if (!persisted) return false;

  const persistedOrderIds = Array.from(
    new Set([...(persisted.orderIds ?? []), persisted.orderId ?? ""]),
  )
    .filter(Boolean)
    .sort();
  const optimisticOrderIds = Array.from(
    new Set([...(optimistic.orderIds ?? []), optimistic.orderId ?? ""]),
  )
    .filter(Boolean)
    .sort();

  return (
    persisted.type.toLowerCase() === optimistic.type.toLowerCase() &&
    persisted.id === optimistic.id &&
    (persisted.productId ?? "") === (optimistic.productId ?? "") &&
    persistedOrderIds.join("|") === optimisticOrderIds.join("|")
  );
}

function isPersistedVersionOfPendingMessage(
  persisted: OrderMessageView,
  pending: OrderMessageView,
): boolean {
  if (persisted.id === pending.id) return true;
  if (persisted.message.trim() !== pending.message.trim()) return false;
  if (
    normalizeRole(persisted.senderRole) !== normalizeRole(pending.senderRole)
  ) {
    return false;
  }
  if (
    pending.senderId &&
    persisted.senderId &&
    persisted.senderId !== pending.senderId
  ) {
    return false;
  }
  if (!messageContextsMatch(persisted.context, pending.context)) return false;
  if (pending.replyTo) {
    if (
      !persisted.replyTo ||
      persisted.replyTo.messageId !== pending.replyTo.messageId
    ) {
      return false;
    }
  }

  const persistedAt = Date.parse(persisted.createdAt);
  const pendingAt = Date.parse(pending.createdAt);
  return (
    Number.isFinite(persistedAt) &&
    Number.isFinite(pendingAt) &&
    Math.abs(persistedAt - pendingAt) <= MESSAGE_MATCH_WINDOW_MS
  );
}

function shouldReconcileMessageFailure(error: unknown): boolean {
  return (
    !(error instanceof AutopartsApiError) ||
    error.status === 0 ||
    error.status === 408 ||
    error.status >= 500
  );
}

function getMessageSendErrorDescription(error: unknown): string {
  const message =
    error instanceof Error ? error.message.trim() : "Please try again.";

  if (/valid json|invalid json|json body|malformed json/i.test(message)) {
    return "The message request could not be parsed. Please retry the message.";
  }

  return message || "Please try again.";
}

async function reconcilePendingMessage(
  pending: PendingMessage,
): Promise<ShopConversationView | null> {
  for (
    let attempt = 0;
    attempt < MESSAGE_RECONCILIATION_ATTEMPTS;
    attempt += 1
  ) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      MESSAGE_RECONCILIATION_REQUEST_TIMEOUT_MS,
    );

    try {
      const conversation = await autopartsApi.shopConversations.get(
        pending.buyerId,
        pending.vendorId,
        controller.signal,
      );
      if (
        conversation.messages.some((message) =>
          isPersistedVersionOfPendingMessage(message, pending.message),
        )
      ) {
        return conversation;
      }
    } catch {
      // A failed confirmation request should not keep the optimistic message pending.
    } finally {
      window.clearTimeout(timeoutId);
    }

    if (attempt + 1 < MESSAGE_RECONCILIATION_ATTEMPTS) {
      await waitForMessageReconciliation(MESSAGE_RECONCILIATION_DELAY_MS);
    }
  }

  return null;
}

function normalizeRole(value?: string): MessageRole | null {
  const role = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (role === "buyer") return "buyer";
  if (role === "seller" || role === "vendor") return "seller";
  if (
    role === "quality_assessor" ||
    role === "qualityassessor" ||
    role === "assessor" ||
    role === "qa_assessor" ||
    role === "qa"
  ) {
    return "quality_assessor";
  }
  return null;
}

function isQualityAssessorConversation(
  conversation: ShopConversationView,
): boolean {
  return conversation.messages.some(
    (message) => normalizeRole(message.senderRole) === "quality_assessor",
  );
}

function getConversationBuyerDisplayName(
  conversation: ShopConversationView,
): string {
  if (isQualityAssessorConversation(conversation)) {
    const assessorMessage = [...conversation.messages]
      .reverse()
      .find(
        (message) => normalizeRole(message.senderRole) === "quality_assessor",
      );
    const assessorName = assessorMessage?.senderName?.trim();
    if (assessorName) return assessorName;

    const normalizedBuyerName = conversation.buyerName
      .replace(/\s*[·|\-]\s*quality\s+assessor\s*$/i, "")
      .trim();
    return normalizedBuyerName || "Quality Assessor";
  }

  return conversation.buyerName || "Buyer";
}

function getConversationBuyerRoleLabel(
  conversation: ShopConversationView,
): "Quality Assessor" | "Buyer" {
  return isQualityAssessorConversation(conversation)
    ? "Quality Assessor"
    : "Buyer";
}

function findSellerShopConversationTarget(
  candidates: ShopConversationView[],
  buyerId: string,
  productId: string,
): ShopConversationView | undefined {
  return candidates.find((conversation) => {
    if (buyerId && conversation.buyerId !== buyerId) return false;
    if (!productId) return Boolean(buyerId);
    return conversation.messages.some((message) =>
      messageMatchesProductContext(message, productId),
    );
  });
}

function findProductContextMessage(
  conversation: ShopConversationView,
  productId: string,
): OrderMessageView | undefined {
  if (!productId) return undefined;
  return [...conversation.messages]
    .reverse()
    .find((message) => messageMatchesProductContext(message, productId));
}

function getConversationKey(conversation: ShopConversationView): string {
  return conversation.id || `${conversation.buyerId}:${conversation.vendorId}`;
}

function getConversationTimestamp(conversation: ShopConversationView): number {
  const value = conversation.updatedAt || conversation.createdAt;
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function sortConversations(
  conversations: ShopConversationView[],
): ShopConversationView[] {
  return [...conversations].sort(
    (left, right) =>
      getConversationTimestamp(right) - getConversationTimestamp(left),
  );
}

function mergeConversation(
  conversations: ShopConversationView[],
  conversation: ShopConversationView,
): ShopConversationView[] {
  return sortConversations([
    conversation,
    ...conversations.filter(
      (entry) =>
        entry.buyerId !== conversation.buyerId ||
        entry.vendorId !== conversation.vendorId,
    ),
  ]);
}

function collapseShopConversations(
  conversations: ShopConversationView[],
): ShopConversationView[] {
  const merged = new Map<string, ShopConversationView>();

  for (const conversation of conversations) {
    const participantKey = `${conversation.buyerId}:${conversation.vendorId}`;
    const existing = merged.get(participantKey);
    if (!existing) {
      merged.set(participantKey, {
        ...conversation,
        messages: [...conversation.messages],
      });
      continue;
    }

    const messages = new Map(
      existing.messages.map((message) => [message.id, message] as const),
    );
    conversation.messages.forEach((message) => {
      if (!messages.has(message.id)) messages.set(message.id, message);
    });
    const mergedMessages = Array.from(messages.values()).sort((left, right) => {
      const leftTime = Date.parse(left.createdAt);
      const rightTime = Date.parse(right.createdAt);
      return (
        (Number.isFinite(leftTime) ? leftTime : 0) -
        (Number.isFinite(rightTime) ? rightTime : 0)
      );
    });
    const existingUpdatedAt = getConversationTimestamp(existing);
    const incomingUpdatedAt = getConversationTimestamp(conversation);
    const latest =
      incomingUpdatedAt > existingUpdatedAt ? conversation : existing;
    const buyerLastReadAt = [
      existing.buyerLastReadAt,
      conversation.buyerLastReadAt,
    ]
      .filter(Boolean)
      .sort((left, right) => Date.parse(right!) - Date.parse(left!))[0];
    const sellerLastReadAt = [
      existing.sellerLastReadAt,
      conversation.sellerLastReadAt,
    ]
      .filter(Boolean)
      .sort((left, right) => Date.parse(right!) - Date.parse(left!))[0];

    merged.set(participantKey, {
      ...latest,
      buyerName: existing.buyerName || conversation.buyerName,
      sellerName: resolveSellerName(
        existing.sellerName,
        conversation.sellerName,
      ),
      buyerAvatarUrl: existing.buyerAvatarUrl || conversation.buyerAvatarUrl,
      sellerAvatarUrl: existing.sellerAvatarUrl || conversation.sellerAvatarUrl,
      messages: mergedMessages,
      unseenCount: Math.max(existing.unseenCount, conversation.unseenCount),
      buyerLastReadAt,
      sellerLastReadAt,
      createdAt:
        [existing.createdAt, conversation.createdAt]
          .filter(Boolean)
          .sort((left, right) => Date.parse(left!) - Date.parse(right!))[0] ||
        latest.createdAt,
    });
  }

  return sortConversations(Array.from(merged.values()));
}

function formatConversationTime(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  return date.toDateString() === now.toDateString()
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function isGenericSellerName(value?: string): boolean {
  return ["", "seller", "seller shop", "shop"].includes(
    String(value ?? "")
      .trim()
      .toLowerCase(),
  );
}

function resolveSellerName(value?: string, fallback?: string): string {
  const primary = String(value ?? "").trim();
  const secondary = String(fallback ?? "").trim();
  if (!isGenericSellerName(primary)) return primary;
  if (!isGenericSellerName(secondary)) return secondary;
  return primary || secondary || "Seller shop";
}

function buildEmptyBuyerConversation(
  buyer: AuthenticatedUser,
  target: BuyerConversationTarget,
): ShopConversationView {
  return {
    buyerId: buyer.id,
    vendorId: target.vendorId,
    buyerName: buyer.name || "Buyer",
    sellerName: target.sellerName,
    buyerAvatarUrl: resolveUserAvatarUrl(buyer),
    sellerAvatarUrl: target.sellerAvatarUrl,
    messages: [],
    unseenCount: 0,
  };
}

function applyMessageResult(
  conversations: ShopConversationView[],
  result: ShopConversationMessageResult,
  optimisticMessageId: string,
): ShopConversationView[] {
  const existing = conversations.find(
    (conversation) =>
      conversation.buyerId === result.buyerId &&
      conversation.vendorId === result.vendorId,
  );
  const messages = [...(existing?.messages ?? [])];
  const optimisticIndex = messages.findIndex(
    (message) => message.id === optimisticMessageId,
  );
  const persistedIndex = messages.findIndex(
    (message) => message.id === result.message.id,
  );

  if (optimisticIndex >= 0) {
    const optimisticMessage = messages[optimisticIndex];
    messages[optimisticIndex] = {
      ...result.message,
      context: result.message.context ?? optimisticMessage.context,
      replyTo: result.message.replyTo ?? optimisticMessage.replyTo,
    };
  } else if (persistedIndex < 0) {
    messages.push(result.message);
  }

  return mergeConversation(conversations, {
    id: result.id ?? existing?.id,
    buyerId: result.buyerId,
    vendorId: result.vendorId,
    buyerName: result.buyerName || existing?.buyerName || "Buyer",
    sellerName: resolveSellerName(result.sellerName, existing?.sellerName),
    buyerAvatarUrl: result.buyerAvatarUrl || existing?.buyerAvatarUrl,
    sellerAvatarUrl: result.sellerAvatarUrl || existing?.sellerAvatarUrl,
    messages,
    unseenCount: result.unseenCount ?? 0,
    buyerLastReadAt: existing?.buyerLastReadAt,
    sellerLastReadAt: existing?.sellerLastReadAt,
    createdAt: result.createdAt ?? existing?.createdAt,
    updatedAt: result.updatedAt ?? result.message.createdAt,
  });
}

function getProductImage(product: ProductView): string {
  return selectPrimaryProductImage(product);
}

function getAvatarUrl(value: unknown): string | undefined {
  if (!value || typeof value !== "object") return undefined;

  const record = value as Record<string, unknown>;
  for (const key of [
    "avatarUrl",
    "avatar_url",
    "avatar",
    "imageUrl",
    "image_url",
    "logoUrl",
    "logo_url",
  ]) {
    const candidate = record[key];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return undefined;
}

function productToAttachment(
  product: ProductView,
): ConversationProductAttachmentView {
  const imageUrl = getProductImage(product);

  return {
    id: product.id,
    name: product.name || product.title || "Product",
    ...(imageUrl ? { imageUrl } : {}),
    ...(typeof product.salePrice === "number" &&
    Number.isFinite(product.salePrice)
      ? { price: product.salePrice }
      : Number.isFinite(product.price)
        ? { price: product.price }
        : {}),
    ...(product.vendorId ? { vendorId: product.vendorId } : {}),
  };
}

function getContextProducts(
  context?: ConversationMessageContextView,
): ConversationProductAttachmentView[] {
  if (!context) return [];
  if (!["product", "products"].includes(context.type.toLowerCase())) {
    return [];
  }
  if (context.products?.length) return context.products;

  const productId = context.productId?.trim();
  if (!productId) {
    return [];
  }

  return [
    {
      id: productId,
      name: context.label || "Product",
      ...(context.images[0] ? { imageUrl: context.images[0] } : {}),
    },
  ];
}

function getContextOrderIds(
  context?: ConversationMessageContextView,
): string[] {
  if (!context || !["order", "orders"].includes(context.type.toLowerCase())) {
    return [];
  }

  return Array.from(
    new Set([...(context.orderIds ?? []), context.orderId ?? "", context.id]),
  )
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, MAX_SELECTED_ORDERS);
}

function buildProductContext(
  products: ConversationProductAttachmentView[],
): ConversationMessageContextView | undefined {
  const uniqueProducts = Array.from(
    new Map(products.map((product) => [product.id, product])).values(),
  ).slice(0, MAX_SELECTED_PRODUCTS);
  if (uniqueProducts.length === 0) return undefined;

  const images = Array.from(
    new Set(
      uniqueProducts
        .map((product) => product.imageUrl?.trim() ?? "")
        .filter(Boolean),
    ),
  );
  const firstProduct = uniqueProducts[0];

  return {
    type: uniqueProducts.length > 1 ? "products" : "product",
    id:
      uniqueProducts.length > 1
        ? uniqueProducts.map((product) => product.id).join(",")
        : firstProduct.id,
    label:
      uniqueProducts.length > 1
        ? `${uniqueProducts.length} products attached`
        : firstProduct.name,
    images,
    ...(uniqueProducts.length === 1 ? { productId: firstProduct.id } : {}),
    products: uniqueProducts,
  };
}

function isProductMessageContext(
  context?: ConversationMessageContextView,
): context is ConversationMessageContextView {
  return Boolean(
    context && ["product", "products"].includes(context.type.toLowerCase()),
  );
}

function cloneMessageContext(
  context: ConversationMessageContextView,
): ConversationMessageContextView {
  return {
    ...context,
    images: [...context.images],
    ...(context.orderIds ? { orderIds: [...context.orderIds] } : {}),
    ...(context.products
      ? {
          products: context.products.map((product) => ({ ...product })),
        }
      : {}),
  };
}

function getInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "U"
  );
}

function ParticipantAvatar({
  name,
  imageUrl,
  className = "h-9 w-9",
}: {
  name: string;
  imageUrl?: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [imageUrl]);

  if (imageUrl && !failed) {
    return (
      <img
        src={imageUrl}
        alt={`${name} avatar`}
        className={`${className} shrink-0 rounded-full border border-border bg-background object-cover`}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div
      className={`${className} flex shrink-0 items-center justify-center rounded-full border border-border bg-accent/10 text-xs font-semibold text-accent`}
      aria-label={`${name} avatar`}
    >
      {getInitials(name)}
    </div>
  );
}

function MessageReplyPreview({
  reply,
  context,
  ownMessage = false,
}: {
  reply?: ConversationMessageReplyView;
  context?: ConversationMessageContextView;
  ownMessage?: boolean;
}) {
  if (!reply) return null;

  const repliedProductContext = isProductMessageContext(context)
    ? context
    : undefined;

  return (
    <div
      className={`mb-2 overflow-hidden rounded-lg border-l-2 px-2.5 py-2 text-xs ${
        ownMessage
          ? "border-accent-foreground/60 bg-accent-foreground/10"
          : "border-accent bg-muted/70"
      }`}
    >
      <p className="truncate font-semibold">
        {reply.senderName || "Replied message"}
      </p>
      {repliedProductContext ? (
        <div className="mt-2 [&>div]:mb-0">
          <ConversationMessageContext context={repliedProductContext} compact />
        </div>
      ) : null}
      <p className="mt-1 line-clamp-3 whitespace-pre-wrap opacity-80">
        {reply.message}
      </p>
    </div>
  );
}

function isBuyerConversationDetailPath(pathname: string): boolean {
  return (
    /^\/buyer\/products\/[^/]+\/?$/.test(pathname) ||
    /^\/buyer\/shops\/[^/]+\/?$/.test(pathname) ||
    /^\/buyer\/orders\/[^/]+\/?$/.test(pathname)
  );
}

const BUYER_ORDER_GROUP_FIELDS = [
  "checkoutGroupId",
  "checkout_group_id",
  "orderGroupId",
  "order_group_id",
  "parentOrderId",
  "parent_order_id",
  "checkoutId",
  "checkout_id",
  "cartId",
  "cart_id",
  "batchId",
  "batch_id",
  "transactionId",
  "transaction_id",
  "paymentIntentId",
  "payment_intent_id",
  "stripeSessionId",
  "stripe_session_id",
] as const;

function getBuyerOrderGroupKey(order: OrderView): string {
  for (const key of BUYER_ORDER_GROUP_FIELDS) {
    const candidate = order[key];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return String(candidate);
    }
  }

  return order.orderNumber?.trim() || order.id.trim();
}

function getBuyerOrderVendorIds(order: OrderView): Set<string> {
  return new Set(
    [order.vendorId, ...(order.items ?? []).map((item) => item.vendorId)]
      .map((value) => String(value ?? "").trim())
      .filter(Boolean),
  );
}

async function resolveBuyerOrderGroup(
  order: OrderView,
  buyerId: string | undefined,
  vendorId: string,
): Promise<OrderView[]> {
  if (!buyerId?.trim()) return [order];

  try {
    const result = await autopartsApi.orders.list<PageResult<OrderView>>({
      page: 1,
      pageSize: ATTACHMENT_CATALOG_PAGE_SIZE,
      customerId: buyerId,
      attachmentCatalog: true,
      sortBy: "createdAt",
      sortDirection: "desc",
    });
    const groupKey = getBuyerOrderGroupKey(order);
    const groupedOrders = result.items.filter(
      (candidate) =>
        getBuyerOrderGroupKey(candidate) === groupKey &&
        getBuyerOrderVendorIds(candidate).has(vendorId),
    );

    return Array.from(
      new Map(
        [order, ...groupedOrders]
          .filter((candidate) => Boolean(candidate.id?.trim()))
          .map((candidate) => [candidate.id, candidate] as const),
      ).values(),
    ).slice(0, MAX_SELECTED_ORDERS);
  } catch {
    return [order];
  }
}

async function resolveBuyerRouteTarget(
  pathname: string,
  search: string,
  buyerId?: string,
): Promise<BuyerConversationTarget | null> {
  const searchParams = new URLSearchParams(search);
  const explicitVendorId = searchParams.get("vendorId")?.trim() || "";
  const explicitSellerName =
    searchParams.get("sellerName")?.trim() || "Seller shop";
  const explicitSellerAvatarUrl =
    searchParams.get("sellerAvatarUrl")?.trim() || undefined;
  const contextType = searchParams.get("contextType")?.trim().toLowerCase();
  const contextId =
    searchParams.get("contextId")?.trim() ||
    searchParams.get("orderId")?.trim() ||
    searchParams.get("productId")?.trim() ||
    "";
  const explicitImages = searchParams
    .getAll("contextImage")
    .map((image) => image.trim())
    .filter(Boolean);

  if (explicitVendorId) {
    return {
      vendorId: explicitVendorId,
      sellerName: explicitSellerName,
      sellerAvatarUrl: explicitSellerAvatarUrl,
      context:
        contextType && contextId
          ? {
              type: contextType,
              id: contextId,
              label:
                searchParams.get("contextLabel")?.trim() ||
                (contextType === "order"
                  ? `Order ${contextId}`
                  : contextType === "shop"
                    ? explicitSellerName
                    : "Product inquiry"),
              images: explicitImages,
              ...(contextType === "order" ? { orderId: contextId } : {}),
              ...(contextType === "product" ? { productId: contextId } : {}),
            }
          : undefined,
    };
  }

  const productMatch = pathname.match(/^\/buyer\/products\/([^/]+)\/?$/);
  if (productMatch?.[1]) {
    const productId = decodeURIComponent(productMatch[1]);
    const product = await autopartsApi.products.getById<ProductView>(productId);
    const vendorId = String(product.vendorId ?? "").trim();
    if (!vendorId) return null;
    const image = getProductImage(product);
    return {
      vendorId,
      sellerName: resolveSellerName(product.vendorName),
      context: {
        type: "product",
        id: product.id || productId,
        productId: product.id || productId,
        label: product.name || product.title || "Product inquiry",
        images: image ? [image] : [],
        products: [productToAttachment(product)],
      },
    };
  }

  const shopMatch = pathname.match(/^\/buyer\/shops\/([^/]+)\/?$/);
  if (shopMatch?.[1]) {
    const vendorId = decodeURIComponent(shopMatch[1]);
    const vendor = await autopartsApi.vendors
      .getById<VendorProfileView>(vendorId)
      .catch(() => null);
    const shopName =
      vendor?.storeName?.trim() ||
      vendor?.businessName?.trim() ||
      vendor?.name?.trim() ||
      explicitSellerName;

    return {
      vendorId,
      sellerName: resolveSellerName(shopName),
      sellerAvatarUrl: getAvatarUrl(vendor),
      context: {
        type: "shop",
        id: vendorId,
        label: resolveSellerName(shopName),
        images: [],
      },
    };
  }

  const orderMatch = pathname.match(/^\/buyer\/orders\/([^/]+)\/?$/);
  if (orderMatch?.[1]) {
    const orderId = decodeURIComponent(orderMatch[1]);
    const order = await autopartsApi.orders.getById(orderId);
    const vendorId = String(
      order.vendorId ?? order.items?.[0]?.vendorId ?? "",
    ).trim();
    if (!vendorId) return null;
    const groupedOrders = await resolveBuyerOrderGroup(
      order,
      buyerId,
      vendorId,
    );
    const context =
      buildOrdersMessageContext(groupedOrders, vendorId) ??
      buildOrderMessageContext(order, vendorId);

    return {
      vendorId,
      sellerName: resolveSellerName(order.vendorName),
      context,
    };
  }

  return null;
}

function normalizeAttachmentIdentity(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeAttachmentVendorIds(
  vendorIds?: string | string[],
): Set<string> {
  const values = Array.isArray(vendorIds) ? vendorIds : [vendorIds];
  return new Set(values.map(normalizeAttachmentIdentity).filter(Boolean));
}

function getOrderProductAttachments(
  order: OrderView,
  vendorIds?: string | string[],
): ConversationProductAttachmentView[] {
  const normalizedVendorIds = normalizeAttachmentVendorIds(vendorIds);
  const allItems = order.items ?? [];
  const matchingItems = allItems.filter((item) => {
    if (normalizedVendorIds.size === 0) return true;
    const itemVendorId = normalizeAttachmentIdentity(
      item.vendorId || order.vendorId,
    );
    return Boolean(itemVendorId && normalizedVendorIds.has(itemVendorId));
  });
  const items = matchingItems.length > 0 ? matchingItems : allItems;

  if (items.length > 0) {
    return items.map((item, index) => ({
      id:
        normalizeAttachmentIdentity(item.productId) ||
        normalizeAttachmentIdentity(order.productId) ||
        `${order.id}:item-${index + 1}`,
      name: item.productName || order.productName || "Product",
      ...(selectPrimaryProductImage(item.productImage, order.productImage)
        ? { imageUrl: selectPrimaryProductImage(item.productImage, order.productImage) }
        : {}),
      ...(Number.isFinite(item.unitPrice) ? { price: item.unitPrice } : {}),
      ...(item.vendorId || order.vendorId
        ? { vendorId: item.vendorId || order.vendorId }
        : {}),
    }));
  }

  return [
    {
      id: order.productId || order.id,
      name:
        order.productName ||
        order.displayName ||
        (order.orderNumber ? `Order ${order.orderNumber}` : "Order item"),
      ...(selectPrimaryProductImage(order.productImage)
        ? { imageUrl: selectPrimaryProductImage(order.productImage) }
        : {}),
      ...(order.vendorId ? { vendorId: order.vendorId } : {}),
    },
  ];
}

function orderMatchesAttachmentSeller(
  order: OrderView,
  vendorAliases: Set<string>,
  sellerProductIds: Set<string>,
): boolean {
  const orderVendorIds = [
    order.vendorId,
    ...(order.items ?? []).map((item) => item.vendorId),
  ]
    .map(normalizeAttachmentIdentity)
    .filter(Boolean);

  if (orderVendorIds.some((vendorId) => vendorAliases.has(vendorId))) {
    return true;
  }

  const orderProductIds = [
    order.productId,
    ...(order.items ?? []).map((item) => item.productId),
  ]
    .map(normalizeAttachmentIdentity)
    .filter(Boolean);

  return orderProductIds.some((productId) => sellerProductIds.has(productId));
}

function buildOrderMessageContext(
  order: OrderView,
  vendorIds?: string | string[],
): ConversationMessageContextView {
  const products = getOrderProductAttachments(order, vendorIds);
  const images = Array.from(
    new Set(
      products.map((product) => product.imageUrl?.trim() ?? "").filter(Boolean),
    ),
  );

  return {
    type: "order",
    id: order.id,
    orderId: order.id,
    label: `Order ${order.orderNumber || order.id}`,
    images,
    products,
  };
}

function buildOrdersMessageContext(
  orders: OrderView[],
  vendorIds?: string | string[],
): ConversationMessageContextView | undefined {
  const uniqueOrders = Array.from(
    new Map(
      orders
        .filter((order) => Boolean(order.id?.trim()))
        .map((order) => [order.id, order] as const),
    ).values(),
  ).slice(0, MAX_SELECTED_ORDERS);

  if (uniqueOrders.length === 0) return undefined;
  if (uniqueOrders.length === 1) {
    return buildOrderMessageContext(uniqueOrders[0], vendorIds);
  }

  const products = Array.from(
    new Map(
      uniqueOrders
        .flatMap((order) => getOrderProductAttachments(order, vendorIds))
        .map((product) => [product.id, product] as const),
    ).values(),
  ).slice(0, MAX_SELECTED_PRODUCTS);
  const images = Array.from(
    new Set(
      products.map((product) => product.imageUrl?.trim() ?? "").filter(Boolean),
    ),
  );
  const orderLabels = uniqueOrders.map(
    (order) => order.orderNumber?.trim() || order.id,
  );
  const visibleLabels = orderLabels.slice(0, 3);
  const remainingCount = orderLabels.length - visibleLabels.length;

  return {
    type: "orders",
    id: uniqueOrders[0].id,
    orderId: uniqueOrders[0].id,
    label: `${uniqueOrders.length} orders attached: ${visibleLabels.join(", ")}${
      remainingCount > 0 ? ` +${remainingCount} more` : ""
    }`,
    images,
    orderIds: uniqueOrders.map((order) => order.id),
    products,
  };
}

function buildOrderRequestMessageContext(
  order: OrderView,
  attachedOrders: OrderView[],
  vendorId?: string,
): ConversationMessageContextView {
  const context =
    buildOrdersMessageContext(attachedOrders, vendorId) ??
    buildOrderMessageContext(order, vendorId);

  if (attachedOrders.length <= 1) return context;

  return {
    ...context,
    type: "order",
    id: order.id,
    orderId: order.id,
  };
}

function getOrderSellerName(order?: OrderView | null): string {
  return order?.vendorName?.trim() || "Seller";
}

function getOrderMessageServerReadAt(
  order: OrderView,
  role: OrderMessageReaderRole,
  explicitVendorId?: string,
): string | undefined {
  const vendorId = explicitVendorId || order.vendorId;
  return (role === "seller"
    ? vendorId && order.sellerMessagesReadAtByVendor?.[vendorId]
    : vendorId && order.courierMessagesReadAtByVendor?.[vendorId]) ||
    (getOrderVendorIDs(order).length === 1 ? (role === "seller" ? order.sellerMessagesReadAt : order.courierMessagesReadAt) : undefined);
}

function getOrderConversationReadAt(
  orders: OrderView[],
  userId: string,
  role: OrderMessageReaderRole,
  vendorId?: string,
): string | undefined {
  if (orders.length === 0) return undefined;

  const readTimestamps = orders.map((order) =>
    resolveOrderMessageReadAt(
      userId,
      role,
      order.id,
      (role === "seller" && vendorId ? order.sellerMessagesReadAtByVendor?.[vendorId] : undefined) || getOrderMessageServerReadAt(order, role, vendorId),
      vendorId || order.vendorId,
    ),
  );
  if (readTimestamps.some((timestamp) => timestamp <= 0)) return undefined;

  return new Date(Math.min(...readTimestamps)).toISOString();
}

function stableMessageNotificationHash(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function getOrderMessageNotificationKey(
  message: OrderMessageView,
  threadKey: string,
): string {
  const contextLabel = message.context?.label?.trim() || "";
  const isMultiOrderMessage =
    message.context?.type.trim().toLowerCase() === "orders" ||
    /^\d+\s+orders?\s+attached:/i.test(contextLabel);
  if (!isMultiOrderMessage) {
    return `${threadKey}:message:${message.id}`;
  }

  const createdAt = Date.parse(message.createdAt);
  const timeBucket = Math.floor(
    (Number.isFinite(createdAt) ? createdAt : 0) / 10_000,
  );
  const fingerprint = [
    message.senderId || "",
    message.senderRole,
    message.message.trim(),
    contextLabel,
    timeBucket,
  ].join("|");
  return `${threadKey}:multi:${stableMessageNotificationHash(fingerprint)}`;
}

function hydrateOrderConversationMessage(
  message: OrderMessageView,
  order: OrderView,
  vendorId?: string,
): OrderMessageView {
  // Old multi-order context cards must never reintroduce a second order.
  if (!message.context) return message;
  const kind = message.context.type.trim().toLowerCase();
  if (kind !== "order" && kind !== "orders") return message;
  return { ...message, context: buildOrderMessageContext(order, vendorId) };
}

function isOrderAssignedToCourier(
  order: OrderView,
  courier: AuthenticatedUser,
): boolean {
  const assignedCourierId = order.courierId || order.courier?.courierId;
  if (assignedCourierId && assignedCourierId === courier.id) return true;

  const assignedEmail = String(order.courierEmail || order.courier?.email || "")
    .trim()
    .toLowerCase();
  const courierEmail = String(courier.email || "")
    .trim()
    .toLowerCase();
  return Boolean(
    assignedEmail && courierEmail && assignedEmail === courierEmail,
  );
}

function getOrderConversationTimestamp(order: OrderView): number {
  const lastMessage = getLastItem(
    filterCourierSellerOrderMessages(order.messages),
  );
  const value = lastMessage?.createdAt || order.updatedAt || order.createdAt;
  const timestamp = value ? Date.parse(value) : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function isOrderOwnedByVendor(order: OrderView, vendorId: string): boolean {
  const normalizedVendorId = vendorId.trim();
  if (!normalizedVendorId) return false;
  if (order.vendorId?.trim() === normalizedVendorId) return true;
  return (order.items ?? []).some(
    (item) => item.vendorId?.trim() === normalizedVendorId,
  );
}

function getOrderCourierName(order?: OrderView | null): string {
  return (
    order?.courierName?.trim() || order?.courier?.name?.trim() || "Courier"
  );
}

function getMessageRoleAvatar(
  messages: OrderMessageView[] | undefined,
  role: "seller" | "courier",
): string | undefined {
  return [...(messages ?? [])]
    .reverse()
    .find(
      (message) =>
        String(message.senderRole).trim().toLowerCase() === role &&
        Boolean(message.senderAvatarUrl?.trim()),
    )
    ?.senderAvatarUrl?.trim();
}

function getOrderSellerAvatar(order?: OrderView | null): string | undefined {
  if (!order) return undefined;

  const record = order as Record<string, unknown>;
  for (const key of [
    "sellerAvatarUrl",
    "seller_avatar_url",
    "vendorAvatarUrl",
    "vendor_avatar_url",
  ]) {
    const candidate = record[key];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return (
    getAvatarUrl(record.vendor) ||
    getAvatarUrl(record.seller) ||
    getMessageRoleAvatar(order.messages, "seller")
  );
}

function getOrderCourierAvatar(order?: OrderView | null): string | undefined {
  if (!order) return undefined;

  const record = order as Record<string, unknown>;
  for (const key of [
    "courierAvatarUrl",
    "courier_avatar_url",
    "driverAvatarUrl",
    "driver_avatar_url",
  ]) {
    const candidate = record[key];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return (
    getAvatarUrl(order.courier) ||
    getMessageRoleAvatar(order.messages, "courier")
  );
}

function isMessageReadAtOrAfter(
  message: OrderMessageView,
  readAt?: string,
): boolean {
  const messageTimestamp = Date.parse(message.createdAt);
  const readTimestamp = readAt ? Date.parse(readAt) : Number.NaN;

  return (
    Number.isFinite(messageTimestamp) &&
    Number.isFinite(readTimestamp) &&
    readTimestamp >= messageTimestamp
  );
}

function getOrderMessageGroupKey(message: OrderMessageView): string {
  const contextLabel = message.context?.label?.trim() || "";
  const isMultiOrderMessage =
    message.context?.type.trim().toLowerCase() === "orders" ||
    /^\d+\s+orders?\s+attached:/i.test(contextLabel);

  if (!isMultiOrderMessage) return `message:${message.id}`;

  const createdAt = Date.parse(message.createdAt);
  const timeBucket = Math.floor(
    (Number.isFinite(createdAt) ? createdAt : 0) / 10_000,
  );
  const expectedOrderCount =
    contextLabel.match(/^(\d+)\s+orders?/i)?.[1] || "";
  return `multi:${stableMessageNotificationHash(
    [
      message.senderId || "",
      message.senderRole,
      message.message.trim(),
      expectedOrderCount,
      timeBucket,
    ].join("|"),
  )}`;
}

function isOrderMessageSeenByRole(
  message: OrderMessageView,
  orders: OrderView[],
  readerRole: OrderMessageReaderRole,
  vendorId?: string,
): boolean {
  const messageKey = getOrderMessageGroupKey(message);
  const relatedOrders = orders.filter((order) =>
    filterCourierSellerOrderMessages(order.messages).some(
      (candidate) => getOrderMessageGroupKey(candidate) === messageKey,
    ),
  );

  return (
    relatedOrders.length > 0 &&
    relatedOrders.every((order) =>
      isMessageReadAtOrAfter(
        message,
        getOrderMessageServerReadAt(order, readerRole, vendorId),
      ),
    )
  );
}

function SeenByAvatar({
  name,
  imageUrl,
}: {
  name: string;
  imageUrl?: string;
}) {
  return (
    <div
      className="mt-1 flex justify-end pr-10"
      title={`Seen by ${name}`}
      aria-label={`Seen by ${name}`}
    >
      <ParticipantAvatar
        name={name}
        imageUrl={imageUrl}
        className="h-4 w-4 border-background text-[7px] ring-1 ring-background"
      />
    </div>
  );
}

function CourierOrderFloatingMessage({
  currentUser,
  pathname,
  search,
}: FloatingMessageProps & { currentUser: AuthenticatedUser }) {
  const [orders, setOrders] = useState<OrderView[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showBuyerThreads, setShowBuyerThreads] = useState(false);
  const sellerFiles = useChatAttachments();
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [messageDraft, setMessageDraft] = useState("");
  const [replyToMessage, setReplyToMessage] = useState<OrderMessageView | null>(
    null,
  );
  const [swipeReplyOffset, setSwipeReplyOffset] =
    useState<SwipeReplyOffset | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [readStateVersion, setReadStateVersion] = useState(0);
  const messageLauncher = useDraggableFloating(
    "sparelynk-floating-message-position",
    { id: "messages", stackIndex: FLOATING_LAUNCHER_STACK.messages },
  );
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const swipeStartRef = useRef<SwipeReplyGesture | null>(null);
  const handledAutoOpenRef = useRef("");
  const markedReadRef = useRef<Set<string>>(new Set());
  const sellerAvatarCacheRef = useRef<Map<string, string | null>>(new Map());
  const sellerAvatarRequestCacheRef = useRef<
    Map<string, Promise<string | undefined>>
  >(new Map());
  const currentCourierAvatar = resolveUserAvatarUrl(currentUser);

  const resolveSellerAvatar = useCallback(
    async (order: OrderView): Promise<string | undefined> => {
      const directAvatar = getOrderSellerAvatar(order);
      if (directAvatar) return directAvatar;

      const vendorId = order.vendorId?.trim();
      if (!vendorId || vendorId === SUPPORT_VENDOR_ID) return undefined;

      const cacheKey = `vendor:${vendorId}`;
      if (sellerAvatarCacheRef.current.has(cacheKey)) {
        return sellerAvatarCacheRef.current.get(cacheKey) ?? undefined;
      }

      const pendingRequest = sellerAvatarRequestCacheRef.current.get(cacheKey);
      if (pendingRequest) return pendingRequest;

      const request = (async () => {
        try {
          const vendor =
            await autopartsApi.vendors.getById<VendorProfileView>(vendorId);
          const vendorAvatar = getAvatarUrl(vendor);
          if (vendorAvatar) {
            sellerAvatarCacheRef.current.set(cacheKey, vendorAvatar);
            return vendorAvatar;
          }

          // Other users' private /users/{id} records are not readable by buyers.
          // Use the public vendor avatar or the existing order snapshot only.
          sellerAvatarCacheRef.current.set(cacheKey, null);
          return undefined;
        } catch {
          sellerAvatarCacheRef.current.set(cacheKey, null);
          return undefined;
        } finally {
          sellerAvatarRequestCacheRef.current.delete(cacheKey);
        }
      })();

      sellerAvatarRequestCacheRef.current.set(cacheKey, request);
      return request;
    },
    [],
  );

  const loadOrders = useCallback(
    async (refresh = false) => {
      if (refresh) setIsRefreshing(true);
      else setIsLoading(true);

      try {
        const response = await autopartsApi.orders.list<PageResult<OrderView>>({
          page: 1,
          pageSize: 200,
          summary: false,
        });
        const assigned = await Promise.all(
          response.items
            .filter((order) => isOrderAssignedToCourier(order, currentUser))
            .flatMap((order) => getOrderVendorIDs(order).map(async (vendorId) => {
              const vendorName = order.vendorId === vendorId && order.vendorName?.trim()
                ? order.vendorName
                : await autopartsApi.vendors.getById<VendorProfileView>(vendorId)
                    .then((vendor) => vendor.storeName || vendor.businessName || vendor.name || "Seller")
                    .catch(() => "Seller");
              const scopedOrder: OrderView = {
                ...order,
                vendorId,
                vendorName,
                items: (order.items ?? []).filter((item) => item.vendorId === vendorId),
                sellerMessagesReadAt: order.sellerMessagesReadAtByVendor?.[vendorId] ?? (getOrderVendorIDs(order).length === 1 ? order.sellerMessagesReadAt : undefined),
                courierMessagesReadAt: order.courierMessagesReadAtByVendor?.[vendorId] ?? (getOrderVendorIDs(order).length === 1 ? order.courierMessagesReadAt : undefined),
                messages: filterCourierSellerOrderMessages(order.messages).filter((message) => getEffectiveOrderVendorID(order, message) === vendorId),
              };
              const sellerAvatarUrl = await resolveSellerAvatar(scopedOrder);
              return {
                ...scopedOrder,
                ...(sellerAvatarUrl ? { sellerAvatarUrl } : {}),
                ...(currentCourierAvatar ? { courierAvatarUrl: currentCourierAvatar } : {}),
                messages: (scopedOrder.messages ?? []).map((message) => ({
                  ...message,
                  senderAvatarUrl: message.senderAvatarUrl ||
                    (String(message.senderRole).trim().toLowerCase() === "seller" ? sellerAvatarUrl : currentCourierAvatar),
                })),
              };
            })),
        );
        assigned.sort(
          (left, right) =>
            getOrderConversationTimestamp(right) -
            getOrderConversationTimestamp(left),
        );
        setOrders(assigned);
      } catch (error) {
        if (refresh) {
          toast.error("Unable to load messages", {
            description:
              error instanceof Error ? error.message : "Please try again.",
          });
        }
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [currentCourierAvatar, currentUser, resolveSellerAvatar],
  );

  useEffect(() => {
    void loadOrders();
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") void loadOrders();
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [loadOrders]);

  useEffect(() => {
    const handleReadState = () => setReadStateVersion((value) => value + 1);
    window.addEventListener(ORDER_MESSAGE_READ_STATE_EVENT, handleReadState);
    window.addEventListener("storage", handleReadState);
    return () => {
      window.removeEventListener(
        ORDER_MESSAGE_READ_STATE_EVENT,
        handleReadState,
      );
      window.removeEventListener("storage", handleReadState);
    };
  }, []);

  const selectedOrder = useMemo(
    () => orders.find((order) => getOrderSellerConversationKey(order) === selectedOrderId) ??
      orders.find((order) => order.id === selectedOrderId &&
        (!new URLSearchParams(search).get("vendorId") || order.vendorId === new URLSearchParams(search).get("vendorId"))) ?? null,
    [orders, selectedOrderId, search],
  );

  const selectableAttachmentOrders = selectedOrder ? [selectedOrder] : [];
  const selectedAttachmentContext = selectedOrder ? buildOrderMessageContext(selectedOrder) : undefined;

  const sellerConversations = useMemo<CourierSellerConversationGroup[]>(() =>
    orders.map((order) => {
      const messages = filterCourierSellerOrderMessages(order.messages).map((message) => hydrateOrderConversationMessage(message, order));
      return {
        key: getOrderSellerConversationKey(order),
        orders: [order],
        order,
        messages,
        lastMessage: getLastItem(messages),
        unseenCount: countUnreadOrderMessages(messages, currentUser.id, "courier", order.id, getOrderMessageServerReadAt(order, "courier"), order.vendorId),
        sellerName: getOrderSellerName(order),
        sellerAvatarUrl: getOrderSellerAvatar(order),
      };
    }).sort((left, right) => getOrderConversationTimestamp(right.order) - getOrderConversationTimestamp(left.order)),
    [currentUser.id, orders, readStateVersion]);

  const filteredSellerConversations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return sellerConversations;

    return sellerConversations.filter(
      ({ orders: conversationOrders, sellerName, lastMessage }) =>
        [
          sellerName,
          lastMessage?.message,
          ...conversationOrders.flatMap((order) => [
            order.orderNumber,
            order.id,
            order.productName,
          ]),
        ].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(query),
        ),
    );
  }, [searchQuery, sellerConversations]);

  const totalUnseen = useMemo(
    () =>
      sellerConversations.reduce(
        (total, conversation) => total + conversation.unseenCount,
        0,
      ),
    [sellerConversations],
  );

  useEffect(() => {
    notifyNewIncomingMessages({
      userId: currentUser.id,
      currentRole: "courier",
      scope: "orders:courier-floating",
      candidates: sellerConversations.flatMap((conversation) => {
        const readAt = getOrderConversationReadAt(
          conversation.orders,
          currentUser.id,
          "courier",
        );
        return conversation.messages
          .filter(
            (message) =>
              String(message.senderRole).trim().toLowerCase() === "seller",
          )
          .map((message) => ({
            key: getOrderMessageNotificationKey(message, conversation.key),
            threadKey: conversation.key,
            senderRole: message.senderRole,
            senderName: message.senderName || conversation.sellerName,
            message: message.message,
            createdAt: message.createdAt,
            readAt,
            imageUrl: selectPrimaryProductImage(
              message.context?.images,
              conversation.order.productImage,
            ),
            href: `/courier/deliveries?messages=open&orderId=${encodeURIComponent(conversation.order.id)}&vendorId=${encodeURIComponent(conversation.order.vendorId || "")}`,
            title: `New message from ${
              message.senderName || conversation.sellerName
            }`,
          }));
      }),
    });
  }, [currentUser.id, sellerConversations]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const orderId = params.get("orderId")?.trim() || "";
    const requestKey = `${pathname}?${search}`;
    const shouldOpen =
      pathname.startsWith("/courier") &&
      params.get("messages") === "open" &&
      Boolean(orderId);

    if (!shouldOpen) {
      handledAutoOpenRef.current = "";
      return;
    }
    if (handledAutoOpenRef.current === requestKey) return;

    handledAutoOpenRef.current = requestKey;
    setIsOpen(true);
    setSelectedOrderId(orderId);
    setShowDetails(true);
  }, [pathname, search]);

  useEffect(() => {
    if (!isOpen || !showDetails || !selectedOrder) return;

    const readAt = new Date().toISOString();
    const readOrderIds = new Set<string>();
    for (const order of selectableAttachmentOrders) {
      const incomingMessages = filterCourierSellerOrderMessages(
        order.messages,
      ).filter(
        (message) =>
          String(message.senderRole).trim().toLowerCase() === "seller",
      );
      const latestIncoming = getLastItem(incomingMessages);
      const markKey = `courier:${order.id}:${order.vendorId}:${latestIncoming?.id || "empty"}`;
      if (markedReadRef.current.has(markKey)) continue;

      markedReadRef.current.add(markKey);
      readOrderIds.add(order.id);
      markOrderMessagesRead(currentUser.id, "courier", order.id, readAt, order.vendorId);
      void autopartsApi.orders
        .markMessagesRead(order.id, {
          readerId: currentUser.id,
          readerRole: "courier",
          channel: "courier_seller",
          vendorId: order.vendorId,
        })
        .then((result) => {
          setOrders((current) =>
            current.map((entry) =>
              entry.id === result.orderId && entry.vendorId === order.vendorId
                ? { ...entry, courierMessagesReadAt: result.readAt }
                : entry,
            ),
          );
        })
        .catch(() => {
          markedReadRef.current.delete(markKey);
        });
    }

    if (readOrderIds.size > 0) {
      setOrders((current) =>
        current.map((order) =>
          readOrderIds.has(order.id) && order.vendorId === selectedOrder.vendorId
            ? { ...order, courierMessagesReadAt: readAt }
            : order,
        ),
      );
    }
    markMessageNotificationThreadRead({
      userId: currentUser.id,
      scope: "orders:courier-floating",
      threadKey: getOrderSellerConversationKey(selectedOrder),
      readAt,
    });
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [
    currentUser.id,
    isOpen,
    selectableAttachmentOrders,
    selectedOrder,
    showDetails,
  ]);

  const selectOrder = (order: OrderView) => {
    setSelectedOrderId(getOrderSellerConversationKey(order));
    setReplyToMessage(null);
    setSwipeReplyOffset(null);
    setMessageDraft("");
    setShowDetails(true);
  };

  const startReply = (message: OrderMessageView) => {
    setReplyToMessage(message);
  };

  const resetMessageSwipe = (event?: ReactPointerEvent<HTMLDivElement>) => {
    if (event?.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    swipeStartRef.current = null;
    setSwipeReplyOffset(null);
  };

  const handleMessagePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
    message: OrderMessageView,
    isOwnMessage: boolean,
  ) => {
    if (
      event.button !== 0 ||
      (event.target instanceof Element && event.target.closest("button"))
    ) {
      return;
    }

    swipeStartRef.current = {
      messageId: message.id,
      pointerId: event.pointerId,
      startX: event.clientX,
      isOwnMessage,
      dragging: false,
    };
    setSwipeReplyOffset({ messageId: message.id, offsetX: 0 });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleMessagePointerMove = (
    event: ReactPointerEvent<HTMLDivElement>,
    message: OrderMessageView,
  ) => {
    const start = swipeStartRef.current;
    if (
      !start ||
      start.messageId !== message.id ||
      start.pointerId !== event.pointerId
    ) {
      return;
    }

    const deltaX = event.clientX - start.startX;
    const directionalDelta = start.isOwnMessage
      ? Math.min(0, deltaX)
      : Math.max(0, deltaX);
    const offsetX = start.isOwnMessage
      ? Math.max(directionalDelta, -SWIPE_REPLY_MAX_OFFSET_PX)
      : Math.min(directionalDelta, SWIPE_REPLY_MAX_OFFSET_PX);

    if (
      !start.dragging &&
      Math.abs(directionalDelta) > SWIPE_DRAG_THRESHOLD_PX
    ) {
      start.dragging = true;
    }
    if (Math.abs(deltaX) > SWIPE_DRAG_THRESHOLD_PX) event.preventDefault();
    setSwipeReplyOffset({ messageId: message.id, offsetX });
  };

  const handleMessagePointerUp = (
    event: ReactPointerEvent<HTMLDivElement>,
    message: OrderMessageView,
  ) => {
    const start = swipeStartRef.current;
    if (
      !start ||
      start.messageId !== message.id ||
      start.pointerId !== event.pointerId
    ) {
      resetMessageSwipe(event);
      return;
    }

    const deltaX = event.clientX - start.startX;
    const shouldReply = start.isOwnMessage
      ? deltaX <= -SWIPE_REPLY_THRESHOLD_PX
      : deltaX >= SWIPE_REPLY_THRESHOLD_PX;

    resetMessageSwipe(event);
    if (shouldReply) startReply(message);
  };

  const sendMessage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const message = messageDraft.trim();
    const order = selectedOrder;
    if ((!message && sellerFiles.files.length === 0) || !order?.vendorId || isSending) return;
    const replyTo = replyToMessage ? { messageId: replyToMessage.id, senderName: replyToMessage.senderName, message: replyToMessage.message } : undefined;
    setIsSending(true);
    try {
      const attachments = await sellerFiles.upload((file, onProgress) =>
        autopartsApi.orders.uploadMessageAttachment(order.id, file, "courier_seller", onProgress, order.vendorId));
      const created = await autopartsApi.orders.sendMessage(order.id, {
        senderId: currentUser.id, senderRole: "courier", senderName: currentUser.name || "Courier",
        channel: "courier_seller", vendorId: order.vendorId, attachments, message, replyTo,
      });
      setOrders((current) => current.map((entry) => entry.id === order.id && entry.vendorId === order.vendorId
        ? { ...entry, messages: [...(entry.messages ?? []), created], updatedAt: created.createdAt } : entry));
      setMessageDraft(""); sellerFiles.clear(); setReplyToMessage(null);
    } catch (error) {
      toast.error("Unable to send message", { description: getMessageSendErrorDescription(error) });
    } finally { sellerFiles.setProgress(null); setIsSending(false); }
  };

  if (!pathname.startsWith("/courier")) return null;

  const selectedMessages = selectedOrder ? filterCourierSellerOrderMessages(selectedOrder.messages).map((message) => hydrateOrderConversationMessage(message, selectedOrder)) : [];
  const selectedSellerName = getOrderSellerName(selectedOrder);
  const selectedSellerAvatarUrl = getOrderSellerAvatar(selectedOrder);
  const latestSelectedMessage = getLastItem(selectedMessages);

  return (
    <>
      <Button
        ref={messageLauncher.ref}
        type="button"
        size="icon"
        data-dragging={messageLauncher.isDragging}
        className={FLOATING_LAUNCHER_CLASS}
        style={messageLauncher.style}
        {...messageLauncher.dragHandlers}
        aria-label={
          totalUnseen > 0
            ? `Open messages, ${totalUnseen} unseen`
            : "Open messages"
        }
        onClick={() => {
          setIsOpen(true);
          setShowDetails(false);
        }}
      >
        <MessageCircle className={FLOATING_LAUNCHER_ICON_CLASS} />
        {totalUnseen > 0 ? (
          <span className="absolute -right-1.5 -top-1.5 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground shadow-md ring-2 ring-background">
            {totalUnseen > 99 ? "99+" : totalUnseen}
          </span>
        ) : null}
      </Button>

      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) {
            setShowDetails(false);
            setMessageDraft("");
            setReplyToMessage(null);
            setSwipeReplyOffset(null);
          }
        }}
      >
        <SheetContent
          side="right"
          className="h-dvh max-h-dvh w-full max-w-none gap-0 overflow-hidden p-0 [&_button]:cursor-pointer sm:inset-y-3 sm:right-3 sm:h-[calc(100dvh_-_1.5rem)] sm:w-[min(560px,calc(100vw_-_1.5rem))] sm:max-w-[560px] sm:rounded-3xl sm:border"
        >
          <SheetHeader className="border-b border-border/60 bg-background/80 px-4 py-3.5 pr-14 backdrop-blur-xl sm:px-5">
            {showDetails && selectedOrder ? (
              <div className="flex min-w-0 items-center gap-2">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="shrink-0"
                  aria-label="Back to conversations"
                  onClick={() => setShowDetails(false)}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <ParticipantAvatar
                  name={selectedSellerName}
                  imageUrl={selectedSellerAvatarUrl}
                />
                <div className="min-w-0">
                  <SheetTitle className="truncate text-sm">
                    {selectedSellerName}
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    Courier and seller order conversation
                  </SheetDescription>
                  <p className="truncate text-xs text-muted-foreground">
                    {`Order ${selectedOrder.orderNumber || selectedOrder.id}`}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <SheetTitle className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-accent" />
                  Messages
                  {totalUnseen > 0 ? (
                    <Badge variant="destructive">{totalUnseen}</Badge>
                  ) : null}
                </SheetTitle>
                <SheetDescription className="sr-only">
                  Courier and seller order conversations
                </SheetDescription>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="mr-7 shrink-0"
                  disabled={isRefreshing}
                  aria-label="Refresh conversations"
                  onClick={() => void loadOrders(true)}
                >
                  {isRefreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}
          </SheetHeader>

          <div className="flex gap-2 border-b px-3 py-2">
            <Button type="button" size="sm" variant={!showBuyerThreads ? "default" : "outline"} onClick={() => { setShowBuyerThreads(false); setShowDetails(false); }}>Sellers</Button>
            <Button type="button" size="sm" variant={showBuyerThreads ? "default" : "outline"} onClick={() => { setShowBuyerThreads(true); setShowDetails(false); }}>Buyers</Button>
          </div>
          {showBuyerThreads ? (
            <OrderChannelPanel user={currentUser} role="courier" assignedOrders={orders} />
          ) : showDetails && selectedOrder ? (
            <section className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-gradient-to-b from-muted/35 via-background to-background p-3 sm:p-5">
                {selectedMessages.length > 0 ? (
                  selectedMessages.map((message) => {
                    const mine =
                      String(message.senderRole).trim().toLowerCase() ===
                      "courier";
                    const senderName = mine
                      ? currentUser.name || "Courier"
                      : message.senderName || selectedSellerName;
                    const senderAvatarUrl =
                      message.senderAvatarUrl ||
                      (mine ? currentCourierAvatar : selectedSellerAvatarUrl);
                    const isLatestMessage =
                      message.id === latestSelectedMessage?.id;
                    const isSeenBySeller =
                      mine &&
                      isLatestMessage &&
                      isOrderMessageSeenByRole(
                        message,
                        selectableAttachmentOrders.length > 0
                          ? selectableAttachmentOrders
                          : selectedOrder
                            ? [selectedOrder]
                            : [],
                        "seller",
                      );
                    const isMessageSwiping =
                      swipeReplyOffset?.messageId === message.id;
                    const isMessageDragging =
                      isMessageSwiping &&
                      swipeStartRef.current?.messageId === message.id &&
                      swipeStartRef.current.dragging;
                    const messageSwipeOffset = isMessageSwiping
                      ? swipeReplyOffset.offsetX
                      : 0;
                    const replyButton = (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 shrink-0 cursor-pointer text-accent opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                        aria-label={`Reply to ${senderName}`}
                        title="Reply"
                        onClick={() => startReply(message)}
                      >
                        <Reply className="h-4 w-4" />
                      </Button>
                    );
                    return (
                      <div key={String(message.id)}>
                        <div
                          className={`group flex items-end gap-2 ${
                            mine ? "justify-end" : "justify-start"
                          }`}
                        >
                        {!mine ? (
                          <ParticipantAvatar
                            name={senderName}
                            imageUrl={senderAvatarUrl}
                            className="h-8 w-8"
                          />
                        ) : null}
                        {mine ? replyButton : null}
                        <div
                          data-dragging={isMessageDragging ? "true" : undefined}
                          className={`min-w-0 max-w-[82%] touch-pan-y select-none sm:max-w-[72%] ${
                            isMessageSwiping
                              ? isMessageDragging
                                ? "cursor-grabbing"
                                : "cursor-grab"
                              : "cursor-grab transition-transform duration-150 ease-out"
                          }`}
                          style={{
                            transform: `translate3d(${messageSwipeOffset}px, 0, 0)`,
                          }}
                          onPointerDown={(event) =>
                            handleMessagePointerDown(event, message, mine)
                          }
                          onPointerMove={(event) =>
                            handleMessagePointerMove(event, message)
                          }
                          onPointerUp={(event) =>
                            handleMessagePointerUp(event, message)
                          }
                          onPointerCancel={(event) => resetMessageSwipe(event)}
                        >
                          <div
                            className={`min-w-0 max-w-full rounded-2xl px-4 py-3 ${
                              mine
                                ? "bg-accent text-accent-foreground"
                                : "border border-border bg-background"
                            }`}
                          >
                            <div className="mb-1 flex items-center justify-between gap-3">
                              <p className="truncate text-xs font-semibold">
                                {mine ? "You" : senderName}
                              </p>
                              <span
                                className={`shrink-0 text-[11px] ${
                                  mine
                                    ? "text-accent-foreground/75"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {formatConversationTime(message.createdAt)}
                              </span>
                            </div>
                            <MessageReplyPreview
                              reply={message.replyTo}
                              context={message.context}
                              ownMessage={mine}
                            />
                            {!message.replyTo ? (
                              <ConversationMessageContext
                                context={message.context}
                                compact
                              />
                            ) : null}
                            <p className="whitespace-pre-wrap wrap-break-word text-sm">
                              {message.message}
                            </p>
                            {message.attachments?.map((attachment) => <ShopMessageAttachment key={attachment.id} attachment={attachment} />)}
                          </div>
                        </div>
                        {!mine ? replyButton : null}
                        {mine ? (
                          <ParticipantAvatar
                            name={senderName}
                            imageUrl={senderAvatarUrl}
                            className="h-8 w-8"
                          />
                        ) : null}
                        </div>
                        {isSeenBySeller ? (
                          <SeenByAvatar
                            name={selectedSellerName}
                            imageUrl={selectedSellerAvatarUrl}
                          />
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <MessageCircle className="h-10 w-10 text-muted-foreground" />
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <form
                className="space-y-2 border-t border-border/60 bg-background/90 p-3 backdrop-blur-xl sm:p-4"
                onSubmit={sendMessage}
              >
                {replyToMessage ? (
                  <div className="relative rounded-lg border border-border bg-muted/40 p-3 pr-10">
                    <div className="flex items-start gap-2">
                      <Reply className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-semibold">
                          Replying to {replyToMessage.senderName || "message"}
                        </p>
                        <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground">
                          {replyToMessage.message}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="absolute right-1.5 top-1.5 h-7 w-7 cursor-pointer"
                      aria-label="Cancel reply"
                      onClick={() => setReplyToMessage(null)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : null}
                <ConversationMessageContext context={selectedAttachmentContext} compact />
                <ChatAttachmentPicker files={sellerFiles.files} progress={sellerFiles.progress} disabled={isSending} onFiles={sellerFiles.addFiles} onRemove={sellerFiles.removeAt} />
                <div className="flex items-end gap-2">
                  <Textarea
                    value={messageDraft}
                    onChange={(event) => setMessageDraft(event.target.value)}
                    placeholder={`Message ${selectedSellerName}...`}
                    rows={2}
                    maxLength={ORDER_MESSAGE_DRAFT_MAX_LENGTH}
                    disabled={isSending}
                    className="min-h-11 resize-none rounded-xl border-border/70 bg-muted/25 px-3 py-2.5 shadow-none focus-visible:bg-background"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    className="h-11 w-11 shrink-0 rounded-xl shadow-sm"
                    disabled={
                      (!messageDraft.trim() && sellerFiles.files.length === 0) || !selectedOrder || isSending
                    }
                    aria-label="Send message"
                  >
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </form>
            </section>
          ) : (
            <section className="flex min-h-0 flex-1 flex-col">
              <div className="border-b border-border p-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search sellers or messages..."
                    className="h-11 rounded-xl border-border/70 bg-muted/30 pl-9 shadow-none focus-visible:bg-background"
                  />
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {isLoading && orders.length === 0 ? (
                  <div className="flex h-full items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredSellerConversations.length === 0 ? (
                  <div className="flex h-full items-center justify-center">
                    <MessageCircle className="h-9 w-9 text-muted-foreground" />
                  </div>
                ) : (
                  filteredSellerConversations.map(
                    ({
                      key,
                      orders: conversationOrders,
                      order,
                      lastMessage,
                      unseenCount,
                      sellerName,
                      sellerAvatarUrl,
                    }) => (
                      <Button variant="ghost"
                        key={String(key)}
                        type="button"
                        className="mx-2 my-1 h-auto w-[calc(100%_-_1rem)] justify-start whitespace-normal rounded-2xl border border-transparent px-3 py-3 text-left transition hover:border-border/70 hover:bg-muted/70"
                        onClick={() => selectOrder(order)}
                      >
                        <div className="flex items-start gap-3">
                          <ParticipantAvatar
                            name={sellerName}
                            imageUrl={sellerAvatarUrl}
                            className="h-10 w-10"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="truncate text-sm font-semibold">
                                {sellerName}
                              </p>
                              <span className="shrink-0 text-[11px] text-muted-foreground">
                                {formatConversationTime(
                                  lastMessage?.createdAt || order.updatedAt,
                                )}
                              </span>
                            </div>
                            <div className="mt-1 flex items-center gap-2">
                              <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                                {`Order ${order.orderNumber || order.id}`} · {lastMessage?.message || "No messages yet"}
                              </p>
                              {unseenCount > 0 ? (
                                <Badge className="h-5 min-w-5 justify-center rounded-full px-1.5 text-[10px]">
                                  {unseenCount > 99 ? "99+" : unseenCount}
                                </Badge>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </Button>
                    ),
                  )
                )}
              </div>
            </section>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

export function FloatingMessage({ pathname, search }: FloatingMessageProps) {
  const [currentUser, setCurrentUser] = useState<AuthenticatedUser | null>(
    () => readStoredAuthSession()?.user ?? null,
  );
  const [vendorProfile, setVendorProfile] = useState<VendorProfileView | null>(
    null,
  );
  const [isOpen, setIsOpen] = useState(false);
  const messageLauncher = useDraggableFloating(
    "sparelynk-floating-message-position",
    { id: "messages", stackIndex: FLOATING_LAUNCHER_STACK.messages },
  );
  const [showConversationDetails, setShowConversationDetails] = useState(false);
  const [activeConversationKind, setActiveConversationKind] =
    useState<ConversationKind>("shop");
  const [conversations, setConversations] = useState<ShopConversationView[]>(
    [],
  );
  const [sellerCourierOrders, setSellerCourierOrders] = useState<OrderView[]>(
    [],
  );
  const [selectedKey, setSelectedKey] = useState("");
  const [selectedCourierOrderId, setSelectedCourierOrderId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [messageDraft, setMessageDraft] = useState("");
  const courierFiles = useChatAttachments();
  const [shopMessageFiles, setShopMessageFiles] = useState<File[]>([]);
  const [shopUploadProgress, setShopUploadProgress] = useState<number | null>(null);
  const [composerContext, setComposerContext] =
    useState<ConversationMessageContextView>();
  const [replyToMessage, setReplyToMessage] = useState<OrderMessageView | null>(
    null,
  );
  const [courierReplyToMessage, setCourierReplyToMessage] =
    useState<OrderMessageView | null>(null);
  const [autoReplyContextMessageId, setAutoReplyContextMessageId] =
    useState("");
  const [availableProducts, setAvailableProducts] = useState<ProductView[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);
  const [buyerOrders, setBuyerOrders] = useState<OrderView[]>([]);
  const [attachmentVendorAliases, setAttachmentVendorAliases] = useState<
    string[]
  >([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isOrderMenuOpen, setIsOrderMenuOpen] = useState(false);
  const [routeTarget, setRouteTarget] =
    useState<BuyerConversationTarget | null>(null);
  const [loadError, setLoadError] = useState("");
  const [courierLoadError, setCourierLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCourierOrders, setIsLoadingCourierOrders] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRefreshingCourierOrders, setIsRefreshingCourierOrders] =
    useState(false);
  const [messagePushState, setMessagePushState] =
    useState<PushNotificationState>("disabled");
  const [isLoadingMessagePushState, setIsLoadingMessagePushState] =
    useState(false);
  const [isUpdatingMessagePushState, setIsUpdatingMessagePushState] =
    useState(false);
  const [isSendingCourierOrderMessage, setIsSendingCourierOrderMessage] =
    useState(false);
  const [orderMessageReadStateVersion, setOrderMessageReadStateVersion] =
    useState(0);
  const [pendingMessageIds, setPendingMessageIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [swipeReplyOffset, setSwipeReplyOffset] =
    useState<SwipeReplyOffset | null>(null);
  const [courierSwipeReplyOffset, setCourierSwipeReplyOffset] =
    useState<SwipeReplyOffset | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const productMenuRef = useRef<HTMLDivElement | null>(null);
  const orderMenuRef = useRef<HTMLDivElement | null>(null);
  const handledMessagePanelSearchRef = useRef("");
  const pendingMessagesRef = useRef<Map<string, PendingMessage>>(new Map());
  const markedReadRef = useRef<Set<string>>(new Set());
  const routeContextSeedRef = useRef("");
  const swipeStartRef = useRef<SwipeReplyGesture | null>(null);
  const courierSwipeStartRef = useRef<SwipeReplyGesture | null>(null);
  const avatarCacheRef = useRef<Map<string, string | null>>(new Map());
  const attachmentProductCatalogCacheRef = useRef<
    Map<string, AttachmentCatalogCacheEntry<ProductView>>
  >(new Map());
  const attachmentOrderCatalogCacheRef = useRef<
    Map<string, OrderAttachmentCatalogCacheEntry>
  >(new Map());
  const conversationLoadSequenceRef = useRef(0);

  const role = normalizeRole(currentUser?.role);
  const isCourierRole = [
    "courier",
    "driver",
    "delivery",
    "delivery_driver",
  ].includes(
    String(currentUser?.role ?? "")
      .trim()
      .toLowerCase(),
  );
  const requestedBuyerId = useMemo(
    () => new URLSearchParams(search).get("buyerId")?.trim() || "",
    [search],
  );
  const isRolePage =
    (role === "buyer" && pathname.startsWith("/buyer")) ||
    (role === "seller" && pathname.startsWith("/seller"));
  const isBuyerContextPage =
    role === "buyer" && isBuyerConversationDetailPath(pathname);
  const sellerParticipantId = vendorProfile?.id?.trim() || "";
  const participantId =
    role === "buyer" ? currentUser?.id.trim() || "" : sellerParticipantId;
  const sellerName =
    vendorProfile?.storeName?.trim() ||
    vendorProfile?.businessName?.trim() ||
    vendorProfile?.name?.trim() ||
    currentUser?.name?.trim() ||
    "Seller";
  const currentUserAvatar = resolveUserAvatarUrl(currentUser);

  useEffect(() => {
    if (role !== "seller" || !currentUser?.id) {
      return;
    }

    let active = true;
    setIsLoadingMessagePushState(true);
    void getPushNotificationState()
      .then((state) => {
        if (active) setMessagePushState(state);
      })
      .catch(() => {
        if (active) setMessagePushState("unavailable");
      })
      .finally(() => {
        if (active) setIsLoadingMessagePushState(false);
      });

    return () => {
      active = false;
    };
  }, [currentUser?.id, role]);

  useEffect(() => {
    const refreshSession = () => {
      setCurrentUser(readStoredAuthSession()?.user ?? null);
    };
    window.addEventListener(AUTH_SESSION_UPDATED_EVENT, refreshSession);
    window.addEventListener("storage", refreshSession);
    return () => {
      window.removeEventListener(AUTH_SESSION_UPDATED_EVENT, refreshSession);
      window.removeEventListener("storage", refreshSession);
    };
  }, []);

  useEffect(() => {
    if (role !== "seller" || !currentUser?.id) {
      setVendorProfile(null);
      return;
    }

    let active = true;
    void autopartsApi.vendors
      .getByUserId<VendorProfileView>(currentUser.id)
      .then((profile) => {
        if (active) setVendorProfile(profile);
      })
      .catch(() => {
        if (active) setVendorProfile(null);
      });
    return () => {
      active = false;
    };
  }, [currentUser?.id, role]);

  const resolveCachedUserAvatar = useCallback(
    async (userId: string): Promise<string | undefined> => {
      // Access to other users' complete profiles is restricted (403).
      // Conversation/message payloads carry public avatar URLs when available.
      return userId === currentUser?.id ? currentUserAvatar : undefined;
    },
    [currentUser?.id, currentUserAvatar],
  );

  const resolveCachedVendorAvatar = useCallback(
    async (vendorId: string): Promise<string | undefined> => {
      // Admin support is a virtual conversation participant, not a vendor
      // profile. A /vendors/sparelynk-admin-support request always returns 404.
      if (vendorId === SUPPORT_VENDOR_ID) return undefined;

      const cacheKey = `vendor:${vendorId}`;
      if (avatarCacheRef.current.has(cacheKey)) {
        return avatarCacheRef.current.get(cacheKey) ?? undefined;
      }

      try {
        const vendor =
          await autopartsApi.vendors.getById<VendorProfileView>(vendorId);
        const directAvatar = getAvatarUrl(vendor);
        if (directAvatar) {
          avatarCacheRef.current.set(cacheKey, directAvatar);
          return directAvatar;
        }

        const userId = vendor.userId?.trim();
        const userAvatar = userId
          ? await resolveCachedUserAvatar(userId)
          : undefined;
        avatarCacheRef.current.set(cacheKey, userAvatar ?? null);
        return userAvatar;
      } catch {
        avatarCacheRef.current.set(cacheKey, null);
        return undefined;
      }
    },
    [resolveCachedUserAvatar],
  );

  const enrichConversationAvatars = useCallback(
    async (loaded: ShopConversationView[]): Promise<ShopConversationView[]> =>
      Promise.all(
        loaded.map(async (conversation) => {
          const buyerAvatarUrl =
            conversation.buyerAvatarUrl ||
            (conversation.buyerId === currentUser?.id
              ? currentUserAvatar
              : await resolveCachedUserAvatar(conversation.buyerId));
          const sellerAvatarUrl =
            conversation.sellerAvatarUrl ||
            (role === "seller"
              ? currentUserAvatar
              : await resolveCachedVendorAvatar(conversation.vendorId));

          return {
            ...conversation,
            buyerAvatarUrl,
            sellerAvatarUrl,
            messages: conversation.messages.map((message) => ({
              ...message,
              senderAvatarUrl:
                message.senderAvatarUrl ||
                (["buyer", "quality_assessor"].includes(normalizeRole(message.senderRole) ?? "")
                  ? buyerAvatarUrl
                  : sellerAvatarUrl),
            })),
          };
        }),
      ),
    [
      currentUser?.id,
      currentUserAvatar,
      resolveCachedUserAvatar,
      resolveCachedVendorAvatar,
      role,
    ],
  );

  const mergePendingMessages = useCallback(
    (loaded: ShopConversationView[]): ShopConversationView[] => {
      let merged = [...loaded];
      pendingMessagesRef.current.forEach((pending) => {
        const existing = merged.find(
          (conversation) =>
            conversation.buyerId === pending.buyerId &&
            conversation.vendorId === pending.vendorId,
        );
        if (
          !existing ||
          existing.messages.some((message) => message.id === pending.message.id)
        ) {
          return;
        }
        merged = mergeConversation(merged, {
          ...existing,
          messages: [...existing.messages, pending.message],
          updatedAt: pending.message.createdAt,
        });
      });
      return merged;
    },
    [],
  );

  const loadConversations = useCallback(
    async ({ refresh = false, target = routeTarget } = {}) => {
      const loadSequence = conversationLoadSequenceRef.current + 1;
      conversationLoadSequenceRef.current = loadSequence;

      if (!role || !participantId || !currentUser) {
        setConversations([]);
        setSelectedKey("");
        setLoadError("");
        return [] as ShopConversationView[];
      }
      if (refresh) setIsRefreshing(true);
      else setIsLoading(true);

      try {
        const loaded =
          role === "buyer"
            ? await autopartsApi.shopConversations.listForBuyer(participantId, {
                forceRefresh: refresh,
              })
            : await autopartsApi.shopConversations.listForSeller(
                participantId,
                { forceRefresh: refresh },
              );
        let next = collapseShopConversations(loaded);
        if ((role === "buyer" || role === "seller") && currentUser) {
          const requesterId = currentUser.id;
          if (!next.some((item) => item.vendorId === SUPPORT_VENDOR_ID && item.buyerId === requesterId)) {
            next = [{
              buyerId: requesterId, vendorId: SUPPORT_VENDOR_ID,
              buyerName: `${currentUser.name || (role === "seller" ? "Seller" : "Buyer")}${role === "seller" ? " · Seller" : ""}`,
              sellerName: "SpareLynk Support", messages: [], unseenCount: 0,
            }, ...next];
          }
          next = [...next].sort((a, b) => Number(b.vendorId === SUPPORT_VENDOR_ID) - Number(a.vendorId === SUPPORT_VENDOR_ID));
        }

        if (role === "buyer" && target?.vendorId) {
          const requested = await autopartsApi.shopConversations
            .get(currentUser.id, target.vendorId)
            .catch(() => buildEmptyBuyerConversation(currentUser, target));
          next = mergeConversation(next, {
            ...requested,
            sellerName: resolveSellerName(
              requested.sellerName,
              target.sellerName,
            ),
          });
        }

        next = mergePendingMessages(collapseShopConversations(next)).sort((a, b) => Number(b.vendorId === SUPPORT_VENDOR_ID) - Number(a.vendorId === SUPPORT_VENDOR_ID));
        if (conversationLoadSequenceRef.current !== loadSequence) {
          return [] as ShopConversationView[];
        }

        // Render the conversation list immediately. Avatar enrichment is kept
        // off the critical path so slow profile requests cannot blank Messages.
        setConversations(next);
        setLoadError("");
        setSelectedKey((current) => {
          if (
            current &&
            next.some(
              (conversation) => getConversationKey(conversation) === current,
            )
          ) {
            return current;
          }
          if (role === "buyer" && target?.vendorId) {
            const requested = next.find(
              (conversation) => conversation.vendorId === target.vendorId,
            );
            if (requested) return getConversationKey(requested);
          }
          if (role === "seller" && requestedBuyerId) {
            const requested = next.find(
              (conversation) => conversation.buyerId === requestedBuyerId,
            );
            if (requested) return getConversationKey(requested);
          }
          return "";
        });

        void enrichConversationAvatars(next)
          .then((enriched) => {
            if (conversationLoadSequenceRef.current !== loadSequence) return;
            setConversations(
              mergePendingMessages(collapseShopConversations(enriched)),
            );
          })
          .catch(() => undefined);
        return next;
      } catch (error) {
        if (conversationLoadSequenceRef.current !== loadSequence) {
          return [] as ShopConversationView[];
        }
        const description =
          error instanceof Error && error.message !== "Failed to fetch"
            ? error.message
            : "Could not reach the conversation service. Please retry.";
        setLoadError(description);
        if (refresh) {
          toast.error("Unable to load conversations", { description });
        }
        return [] as ShopConversationView[];
      } finally {
        if (conversationLoadSequenceRef.current === loadSequence) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [
      currentUser,
      enrichConversationAvatars,
      mergePendingMessages,
      participantId,
      requestedBuyerId,
      role,
      routeTarget,
    ],
  );

  const loadSellerCourierOrders = useCallback(
    async ({ refresh = false } = {}) => {
      if (role !== "seller" || !sellerParticipantId) {
        setSellerCourierOrders([]);
        setSelectedCourierOrderId("");
        setCourierLoadError("");
        return;
      }

      if (refresh) setIsRefreshingCourierOrders(true);
      else setIsLoadingCourierOrders(true);

      try {
        const response = await autopartsApi.orders.list<PageResult<OrderView>>({
          page: 1,
          pageSize: 200,
          vendorId: sellerParticipantId,
          summary: false,
        });
        const scopedOrders = response.items
          .filter((order) => isOrderOwnedByVendor(order, sellerParticipantId))
          .map((order) => ({
            ...order,
            messages: filterCourierSellerOrderMessages(order.messages),
          }))
          .filter(
            (order) =>
              Boolean(
                order.courierId?.trim() ||
                order.courier?.courierId?.trim() ||
                order.courierName?.trim() ||
                order.courier?.name?.trim(),
              ) || order.messages.length > 0,
          );
        const orders = await Promise.all(
          scopedOrders.map(async (order) => {
            const courierUserId = String(
              order.courierId ||
                order.courier?.courierId ||
                order.courier?.userId ||
                "",
            ).trim();
            const courierAvatarUrl =
              getOrderCourierAvatar(order) ||
              (courierUserId
                ? await resolveCachedUserAvatar(courierUserId)
                : undefined);
            const sellerAvatarUrl =
              getOrderSellerAvatar(order) || currentUserAvatar;

            return {
              ...order,
              ...(courierAvatarUrl ? { courierAvatarUrl } : {}),
              ...(sellerAvatarUrl ? { sellerAvatarUrl } : {}),
              messages: order.messages.map((message) => ({
                ...message,
                senderAvatarUrl:
                  message.senderAvatarUrl ||
                  (String(message.senderRole).trim().toLowerCase() ===
                  "courier"
                    ? courierAvatarUrl
                    : sellerAvatarUrl),
              })),
            };
          }),
        );
        orders.sort(
          (left, right) =>
            getOrderConversationTimestamp(right) -
            getOrderConversationTimestamp(left),
        );

        setSellerCourierOrders(orders);
        setCourierLoadError("");
        setSelectedCourierOrderId((current) =>
          current && orders.some((order) => order.id === current)
            ? current
            : "",
        );
      } catch (error) {
        const description =
          error instanceof Error && error.message !== "Failed to fetch"
            ? error.message
            : "Could not reach the courier conversation service. Please retry.";
        setCourierLoadError(description);
        if (refresh) {
          toast.error("Unable to load courier conversations", { description });
        }
      } finally {
        setIsLoadingCourierOrders(false);
        setIsRefreshingCourierOrders(false);
      }
    },
    [
      currentUserAvatar,
      resolveCachedUserAvatar,
      role,
      sellerParticipantId,
    ],
  );

  useEffect(() => {
    if (!isRolePage || !participantId) return;
    void loadConversations({ target: routeTarget });
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void loadConversations({ target: routeTarget });
      }
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [isRolePage, loadConversations, participantId, routeTarget]);

  useEffect(() => {
    if (!isRolePage || role !== "seller" || !sellerParticipantId) return;

    void loadSellerCourierOrders();
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void loadSellerCourierOrders();
      }
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [isRolePage, loadSellerCourierOrders, role, sellerParticipantId]);

  useEffect(() => {
    const handleReadState = () =>
      setOrderMessageReadStateVersion((value) => value + 1);
    window.addEventListener(ORDER_MESSAGE_READ_STATE_EVENT, handleReadState);
    window.addEventListener("storage", handleReadState);
    return () => {
      window.removeEventListener(
        ORDER_MESSAGE_READ_STATE_EVENT,
        handleReadState,
      );
      window.removeEventListener("storage", handleReadState);
    };
  }, []);

  useEffect(() => {
    if (!isRolePage || !participantId || !role) return;

    return autopartsApi.shopConversations.subscribe({
      participantId,
      role,
      onEvent: (event) => {
        const belongsToParticipant =
          role === "buyer"
            ? event.buyerId === participantId
            : event.vendorId === participantId;
        if (!belongsToParticipant) return;

        if (
          event.type === "message.created" ||
          event.type === "conversation.read"
        ) {
          void loadConversations({ target: routeTarget });
        }
      },
    });
  }, [isRolePage, loadConversations, participantId, role, routeTarget]);

  useEffect(() => {
    routeContextSeedRef.current = "";
  }, [pathname, search]);

  useEffect(() => {
    const hasExplicitTarget = Boolean(
      new URLSearchParams(search).get("vendorId")?.trim(),
    );

    if (
      !isRolePage ||
      role !== "buyer" ||
      (!isBuyerContextPage && !hasExplicitTarget)
    ) {
      setRouteTarget(null);
      return;
    }

    let active = true;
    void resolveBuyerRouteTarget(pathname, search, currentUser?.id)
      .then((target) => {
        if (active) setRouteTarget(target);
      })
      .catch(() => {
        if (active) setRouteTarget(null);
      });
    return () => {
      active = false;
    };
  }, [currentUser?.id, isBuyerContextPage, isRolePage, pathname, role, search]);

  useEffect(() => {
    if (isRolePage && pathname.endsWith("/conversations")) {
      setIsOpen(true);
      setActiveConversationKind("shop");
      setSelectedCourierOrderId("");
      setShowConversationDetails(false);
    }
  }, [isRolePage, pathname]);

  useEffect(() => {
    if (!isRolePage) return;

    const params = new URLSearchParams(search);
    if (params.get("messages") !== "open") {
      handledMessagePanelSearchRef.current = "";
      return;
    }

    const searchKey = `${pathname}?${params.toString()}`;
    if (handledMessagePanelSearchRef.current === searchKey) return;
    handledMessagePanelSearchRef.current = searchKey;

    setIsOpen(true);
    if (role === "seller" && params.get("channel") === "courier") {
      const orderId = params.get("orderId")?.trim() || "";
      setActiveConversationKind("courier");
      setSelectedCourierOrderId(orderId);
      setSelectedKey("");
      setShowConversationDetails(Boolean(orderId));
      return;
    }

    const buyerId = params.get("buyerId")?.trim() || "";
    const productId = params.get("productId")?.trim() || "";
    if (role === "seller" && (buyerId || productId)) {
      setActiveConversationKind("shop");
      setSelectedCourierOrderId("");

      void (async () => {
        let targetConversation = findSellerShopConversationTarget(
          conversations,
          buyerId,
          productId,
        );
        if (!targetConversation) {
          const refreshed = await loadConversations({
            refresh: true,
            target: routeTarget,
          });
          targetConversation = findSellerShopConversationTarget(
            refreshed,
            buyerId,
            productId,
          );
        }

        if (!targetConversation) {
          setShowConversationDetails(false);
          return;
        }

        setSelectedKey(getConversationKey(targetConversation));
        const productContextMessage = findProductContextMessage(
          targetConversation,
          productId,
        );
        setComposerContext(
          productContextMessage?.context
            ? cloneMessageContext(productContextMessage.context)
            : undefined,
        );
        setShowConversationDetails(true);
      })();
      return;
    }

    if (!buyerId && !params.get("vendorId")) {
      setActiveConversationKind("shop");
      setSelectedCourierOrderId("");
      setShowConversationDetails(false);
    }
  }, [
    conversations,
    isRolePage,
    loadConversations,
    pathname,
    role,
    routeTarget,
    search,
  ]);

  useEffect(() => {
    if (!isRolePage || !role) return;

    const handleFloatingMessageOpen = (event: Event) => {
      const detail = (event as CustomEvent<FloatingMessageOpenDetail>).detail;
      setIsOpen(true);

      if (role === "buyer" && detail?.channel === "courier") {
        setActiveConversationKind("courier");
        setSelectedCourierOrderId(detail.orderId?.trim() || "");
        setShowConversationDetails(false);
        return;
      }
      if (role === "seller" && detail?.channel === "courier") {
        const orderId = detail.orderId?.trim() || "";
        setActiveConversationKind("courier");
        setSelectedCourierOrderId(orderId);
        setSelectedKey("");
        setShowConversationDetails(Boolean(orderId));
        return;
      }

      if (
        role === "seller" &&
        detail?.channel !== "courier" &&
        (detail?.conversationKey || detail?.buyerId || detail?.productId)
      ) {
        void (async () => {
          const findTargetConversation = (
            candidates: ShopConversationView[],
          ) =>
            candidates.find((conversation) => {
              if (
                detail.conversationKey &&
                getConversationKey(conversation) === detail.conversationKey
              ) {
                return true;
              }
              if (
                detail.buyerId &&
                conversation.buyerId === detail.buyerId &&
                (!detail.vendorId || conversation.vendorId === detail.vendorId)
              ) {
                return true;
              }
              if (detail.productId) {
                return conversation.messages.some(
                  (message) =>
                    normalizeRole(message.senderRole) ===
                      "quality_assessor" &&
                    messageMatchesProductContext(message, detail.productId!),
                );
              }
              return false;
            });

          let targetConversation = findTargetConversation(conversations);
          if (!targetConversation) {
            const refreshed = await loadConversations({
              refresh: true,
              target: routeTarget,
            });
            targetConversation = findTargetConversation(refreshed);
          }

          if (!targetConversation) {
            setActiveConversationKind("shop");
            setSelectedCourierOrderId("");
            setShowConversationDetails(false);
            return;
          }

          setActiveConversationKind("shop");
          setSelectedCourierOrderId("");
          setSelectedKey(getConversationKey(targetConversation));

          const productContextMessage = detail.productId
            ? [...targetConversation.messages]
                .reverse()
                .find((message) =>
                  messageMatchesProductContext(message, detail.productId!),
                )
            : undefined;
          setComposerContext(
            productContextMessage?.context
              ? cloneMessageContext(productContextMessage.context)
              : undefined,
          );
          setShowConversationDetails(true);
        })();
        return;
      }

      if (role === "buyer" && routeTarget?.vendorId && currentUser) {
        const targetConversation = conversations.find(
          (conversation) => conversation.vendorId === routeTarget.vendorId,
        );
        const conversation =
          targetConversation ??
          buildEmptyBuyerConversation(currentUser, routeTarget);

        if (!targetConversation) {
          setConversations((current) =>
            mergeConversation(current, conversation),
          );
        }

        setActiveConversationKind("shop");
        setSelectedCourierOrderId("");
        setSelectedKey(getConversationKey(conversation));
        setComposerContext(
          routeTarget.context
            ? cloneMessageContext(routeTarget.context)
            : undefined,
        );
        routeContextSeedRef.current = routeTarget.context
          ? `${getConversationKey(conversation)}:${routeTarget.context.type}:${routeTarget.context.id}`
          : "";
        setShowConversationDetails(true);
        return;
      }

      setActiveConversationKind("shop");
      setSelectedCourierOrderId("");
      setShowConversationDetails(false);
    };

    window.addEventListener(
      FLOATING_MESSAGE_OPEN_EVENT,
      handleFloatingMessageOpen,
    );
    return () =>
      window.removeEventListener(
        FLOATING_MESSAGE_OPEN_EVENT,
        handleFloatingMessageOpen,
      );
  }, [
    conversations,
    currentUser,
    isRolePage,
    loadConversations,
    role,
    routeTarget,
  ]);

  useEffect(() => {
    if (!role) return;

    const handleNotificationNavigation = (event: Event) => {
      const href = (event as CustomEvent<MessageNotificationNavigationDetail>)
        .detail?.href;
      if (!href) return;

      let target: URL;
      try {
        target = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (target.origin !== window.location.origin) return;

      const targetRole = target.pathname.startsWith("/buyer")
        ? "buyer"
        : target.pathname.startsWith("/seller")
          ? "seller"
          : null;
      if (targetRole !== role) return;

      const targetParams = target.searchParams;
      if (role === "seller" && targetParams.get("channel") === "courier") {
        const orderId = targetParams.get("orderId")?.trim() || "";
        event.preventDefault();
        setIsOpen(true);
        setActiveConversationKind("courier");
        setSelectedCourierOrderId(orderId);
        setSelectedKey("");
        setShowConversationDetails(Boolean(orderId));
        return;
      }

      const targetProductId = targetParams.get("productId")?.trim() || "";
      const targetConversation =
        role === "buyer"
          ? conversations.find(
              (conversation) =>
                conversation.vendorId === targetParams.get("vendorId"),
            )
          : findSellerShopConversationTarget(
              conversations,
              targetParams.get("buyerId")?.trim() || "",
              targetProductId,
            );

      setMessageDraft("");
      setComposerContext(undefined);
      setReplyToMessage(null);
      setAutoReplyContextMessageId("");
      setIsProductMenuOpen(false);
      setIsOrderMenuOpen(false);

      if (!targetConversation) {
        setShowConversationDetails(false);
        return;
      }

      event.preventDefault();
      setIsOpen(true);
      setActiveConversationKind("shop");
      setSelectedCourierOrderId("");
      setSelectedKey(getConversationKey(targetConversation));
      if (role === "seller" && targetProductId) {
        const contextMessage = findProductContextMessage(
          targetConversation,
          targetProductId,
        );
        setComposerContext(
          contextMessage?.context
            ? cloneMessageContext(contextMessage.context)
            : undefined,
        );
      }
      setShowConversationDetails(true);
    };

    window.addEventListener(
      MESSAGE_NOTIFICATION_NAVIGATE_EVENT,
      handleNotificationNavigation,
    );
    return () =>
      window.removeEventListener(
        MESSAGE_NOTIFICATION_NAVIGATE_EVENT,
        handleNotificationNavigation,
      );
  }, [conversations, role, sellerCourierOrders]);

  useEffect(() => {
    if (!currentUser?.id || !role) return;
    notifyNewIncomingMessages({
      userId: currentUser.id,
      currentRole: role,
      scope: `shop-conversations:${role}`,
      candidates: conversations.flatMap((conversation) =>
        conversation.messages.map((message) => ({
          key: `shop:${conversation.buyerId}:${conversation.vendorId}:${message.id}`,
          threadKey: `shop:${conversation.buyerId}:${conversation.vendorId}`,
          senderRole: message.senderRole,
          senderName:
            message.senderName ||
            (role === "buyer"
              ? resolveSellerName(conversation.sellerName)
              : getConversationBuyerDisplayName(conversation)),
          message: message.message,
          createdAt: message.createdAt,
          readAt:
            role === "buyer"
              ? conversation.buyerLastReadAt
              : conversation.sellerLastReadAt,
          imageUrl:
            message.senderAvatarUrl ||
            (["buyer", "quality_assessor"].includes(
              normalizeRole(message.senderRole) ?? "",
            )
              ? conversation.buyerAvatarUrl
              : conversation.sellerAvatarUrl) ||
            message.context?.images[0],
          href:
            role === "buyer"
              ? `/buyer/home?messages=open&vendorId=${encodeURIComponent(
                  conversation.vendorId,
                )}&sellerName=${encodeURIComponent(
                  resolveSellerName(conversation.sellerName),
                )}`
              : buildSellerShopNotificationHref(
                  conversation.buyerId,
                  normalizeRole(message.senderRole) === "quality_assessor"
                    ? getMessageProductContextId(message)
                    : undefined,
                ),
          title:
            role === "seller" &&
            normalizeRole(message.senderRole) === "quality_assessor"
              ? getQualityAssessorRequestNotificationTitle(message)
              : `New message from ${
                  message.senderName ||
                  (role === "buyer"
                    ? resolveSellerName(conversation.sellerName)
                    : getConversationBuyerDisplayName(conversation))
                }`,
        })),
      ),
    });
  }, [conversations, currentUser?.id, role]);

  useEffect(() => {
    if (!currentUser?.id || role !== "seller") return;

    notifyNewIncomingMessages({
      userId: currentUser.id,
      currentRole: "seller",
      scope: "orders:seller-floating",
      candidates: sellerCourierOrders.flatMap((order) =>
        filterCourierSellerOrderMessages(order.messages)
          .filter(
            (message) =>
              String(message.senderRole).trim().toLowerCase() === "courier",
          )
          .map((message) => {
            const threadKey = `order:${order.id}`;
            return {
              key: getOrderMessageNotificationKey(message, threadKey),
              threadKey,
              senderRole: message.senderRole,
              senderName: message.senderName || getOrderCourierName(order),
              message: message.message,
              createdAt: message.createdAt,
              imageUrl:
                message.senderAvatarUrl ||
                getOrderCourierAvatar(order) ||
                selectPrimaryProductImage(
                  message.context?.images,
                  order.productImage,
                ),
              href: `/seller/dashboard?messages=open&channel=courier&orderId=${encodeURIComponent(order.id)}`,
              title: `New message from ${
                message.senderName || getOrderCourierName(order)
              }`,
              readAt: getOrderConversationReadAt(
                [order],
                currentUser.id,
                "seller",
                sellerParticipantId,
              ),
            };
          }),
      ),
    });
  }, [currentUser?.id, role, sellerCourierOrders]);

  const filteredConversations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter((conversation) => {
      const participantName =
        role === "buyer"
          ? resolveSellerName(conversation.sellerName)
          : getConversationBuyerDisplayName(conversation);
      const lastMessage = getLastItem(conversation.messages)?.message || "";
      return (
        participantName.toLowerCase().includes(query) ||
        lastMessage.toLowerCase().includes(query)
      );
    });
  }, [conversations, role, searchQuery]);

  const sellerCourierConversations = useMemo<SellerCourierConversationGroup[]>(() =>
    sellerCourierOrders.map((order) => {
      const messages = filterCourierSellerOrderMessages(order.messages).map((message) => hydrateOrderConversationMessage(message, order, sellerParticipantId));
      return {
        key: getCourierConversationKey(order), orders: [order], order, messages,
        lastMessage: getLastItem(messages),
        unseenCount: currentUser?.id ? countUnreadOrderMessages(messages, currentUser.id, "seller", order.id, getOrderMessageServerReadAt(order, "seller", sellerParticipantId), sellerParticipantId) : 0,
        courierName: getOrderCourierName(order),
        courierAvatarUrl: getOrderCourierAvatar(order),
      };
    }).sort((left, right) => getOrderConversationTimestamp(right.order) - getOrderConversationTimestamp(left.order)),
    [currentUser?.id, orderMessageReadStateVersion, sellerCourierOrders, sellerParticipantId]);

  const filteredSellerCourierConversations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return sellerCourierConversations;

    return sellerCourierConversations.filter(
      ({ orders, courierName, lastMessage }) =>
        [
          courierName,
          lastMessage?.message,
          ...orders.flatMap((order) => [
            order.orderNumber,
            order.id,
            order.productName,
          ]),
        ].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(query),
        ),
    );
  }, [searchQuery, sellerCourierConversations]);

  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (conversation) => getConversationKey(conversation) === selectedKey,
      ) ?? null,
    [conversations, selectedKey],
  );
  const selectedCourierConversation = useMemo(
    () =>
      sellerCourierConversations.find((conversation) =>
        conversation.orders.some(
          (order) => order.id === selectedCourierOrderId,
        ),
      ) ?? null,
    [selectedCourierOrderId, sellerCourierConversations],
  );
  const selectedCourierOrder = useMemo(
    () =>
      selectedCourierConversation?.orders.find(
        (order) => order.id === selectedCourierOrderId,
      ) ??
      selectedCourierConversation?.order ??
      null,
    [selectedCourierConversation, selectedCourierOrderId],
  );
  const selectedCourierMessages = selectedCourierConversation?.messages ?? [];
  const latestSelectedCourierMessage = getLastItem(selectedCourierMessages);
  const latestSelectedShopMessage = getLastItem(selectedConversation?.messages);

  const isSendingSelectedConversation = useMemo(
    () =>
      selectedConversation?.messages.some((message) =>
        pendingMessageIds.has(message.id),
      ) ?? false,
    [pendingMessageIds, selectedConversation],
  );

  useEffect(() => {
    if (
      role === "buyer" &&
      isOpen &&
      routeTarget?.vendorId &&
      selectedConversation?.vendorId === routeTarget.vendorId
    ) {
      setShowConversationDetails(true);
    }
  }, [isOpen, role, routeTarget?.vendorId, selectedConversation?.vendorId]);

  const totalUnseen = useMemo(() => {
    const shopUnseen = conversations.reduce(
      (total, conversation) => total + conversation.unseenCount,
      0,
    );
    const courierUnseen = sellerCourierConversations.reduce(
      (total, conversation) => total + conversation.unseenCount,
      0,
    );
    return shopUnseen + courierUnseen;
  }, [conversations, sellerCourierConversations]);

  const selectedProducts = useMemo(
    () => getContextProducts(composerContext),
    [composerContext],
  );
  const selectedProductIds = useMemo(
    () => new Set(selectedProducts.map((product) => product.id)),
    [selectedProducts],
  );
  const selectedOrderContextIds = useMemo(
    () => getContextOrderIds(composerContext),
    [composerContext],
  );
  const selectedOrderContextIdSet = useMemo(
    () => new Set(selectedOrderContextIds),
    [selectedOrderContextIds],
  );

  useEffect(() => {
    if (role !== "buyer" || !selectedConversation?.vendorId) {
      setAvailableProducts([]);
      setIsLoadingProducts(false);
      return;
    }

    const vendorId = selectedConversation.vendorId;
    const cached = attachmentProductCatalogCacheRef.current.get(vendorId);
    const hasFreshCache = Boolean(cached && cached.expiresAt > Date.now());

    setAvailableProducts(cached?.items ?? []);
    setIsLoadingProducts(hasFreshCache ? false : !cached?.items.length);
    if (hasFreshCache) return;

    let active = true;
    void autopartsApi.products
      .list<PageResult<ProductView>>({
        page: 1,
        pageSize: ATTACHMENT_CATALOG_PAGE_SIZE,
        vendorId,
        listingState: "LIVE",
        summary: true,
        attachmentCatalog: true,
        sortBy: "createdAt",
        sortDirection: "desc",
      })
      .then((result) => {
        const items = result.items.filter(
          (product) => !product.vendorId || product.vendorId === vendorId,
        );
        attachmentProductCatalogCacheRef.current.set(vendorId, {
          expiresAt: Date.now() + ATTACHMENT_CATALOG_CACHE_TTL_MS,
          items,
        });
        if (active) setAvailableProducts(items);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setIsLoadingProducts(false);
      });

    return () => {
      active = false;
    };
  }, [role, selectedConversation?.vendorId]);

  useEffect(() => {
    if (
      role !== "buyer" ||
      !currentUser?.id ||
      !selectedConversation?.vendorId
    ) {
      setBuyerOrders([]);
      setAttachmentVendorAliases([]);
      setIsLoadingOrders(false);
      return;
    }

    const customerId = currentUser.id;
    const vendorId = selectedConversation.vendorId;
    const cacheKey = `${customerId}:${vendorId}`;
    const cached = attachmentOrderCatalogCacheRef.current.get(cacheKey);
    const hasFreshCache = Boolean(cached && cached.expiresAt > Date.now());

    setBuyerOrders(cached?.items ?? []);
    setAttachmentVendorAliases(cached?.vendorAliases ?? [vendorId]);
    setIsLoadingOrders(hasFreshCache ? false : !cached?.items.length);
    if (hasFreshCache) return;

    let active = true;
    const vendorRequest = autopartsApi.vendors
      .getById<VendorProfileView>(vendorId)
      .catch(() => null);

    const loadCatalogForVendor = (attachmentVendorId: string) =>
      autopartsApi.orders.list<PageResult<OrderView>>({
        page: 1,
        pageSize: ATTACHMENT_CATALOG_PAGE_SIZE,
        customerId,
        vendorId: attachmentVendorId,
        summary: true,
        attachmentCatalog: true,
        sortBy: "createdAt",
        sortDirection: "desc",
      });

    void (async () => {
      let mergedOrders = cached?.items ?? [];
      try {
        const primaryResult = await loadCatalogForVendor(vendorId);
        mergedOrders = primaryResult.items;
        if (active) {
          setBuyerOrders(mergedOrders);
          setIsLoadingOrders(false);
        }
      } catch {
        // Keep stale cached orders visible while alternate vendor identities load.
      }

      const vendor = await vendorRequest;
      const vendorAliases = Array.from(
        new Set(
          [vendorId, vendor?.id, vendor?.userId]
            .map(normalizeAttachmentIdentity)
            .filter(Boolean),
        ),
      );
      const alternateAliases = vendorAliases.filter(
        (alias) => alias !== vendorId,
      );

      if (active) setAttachmentVendorAliases(vendorAliases);

      if (alternateAliases.length > 0) {
        const alternateResults = await Promise.allSettled(
          alternateAliases.map((alias) => loadCatalogForVendor(alias)),
        );
        const alternateOrders = alternateResults.flatMap((result) =>
          result.status === "fulfilled" ? result.value.items : [],
        );
        mergedOrders = Array.from(
          new Map(
            [...mergedOrders, ...alternateOrders].map(
              (order) => [order.id, order] as const,
            ),
          ).values(),
        ).sort((left, right) => {
          const leftTime = Date.parse(left.createdAt || "");
          const rightTime = Date.parse(right.createdAt || "");
          return (
            (Number.isFinite(rightTime) ? rightTime : 0) -
            (Number.isFinite(leftTime) ? leftTime : 0)
          );
        });
      }

      attachmentOrderCatalogCacheRef.current.set(cacheKey, {
        expiresAt: Date.now() + ATTACHMENT_CATALOG_CACHE_TTL_MS,
        items: mergedOrders,
        vendorAliases,
      });
      if (active) {
        setBuyerOrders(mergedOrders);
        setAttachmentVendorAliases(vendorAliases);
        setIsLoadingOrders(false);
      }
    })().catch(() => {
      if (active) setIsLoadingOrders(false);
    });

    return () => {
      active = false;
    };
  }, [currentUser?.id, role, selectedConversation?.vendorId]);

  const availableOrders = useMemo(() => {
    const vendorAliases = new Set(attachmentVendorAliases);
    const sellerProductIds = new Set(
      availableProducts
        .map((product) => normalizeAttachmentIdentity(product.id))
        .filter(Boolean),
    );

    return buyerOrders
      .filter((order) =>
        orderMatchesAttachmentSeller(
          order,
          vendorAliases,
          sellerProductIds,
        ),
      )
      .sort((left, right) => {
        const leftTime = Date.parse(left.createdAt || "");
        const rightTime = Date.parse(right.createdAt || "");
        return (
          (Number.isFinite(rightTime) ? rightTime : 0) -
          (Number.isFinite(leftTime) ? leftTime : 0)
        );
      });
  }, [attachmentVendorAliases, availableProducts, buyerOrders]);

  useEffect(() => {
    const context = routeTarget?.context;
    if (
      role !== "buyer" ||
      !context ||
      !selectedConversation ||
      selectedConversation.vendorId !== routeTarget.vendorId
    ) {
      return;
    }

    const seedKey = `${getConversationKey(selectedConversation)}:${context.type}:${context.id}`;
    if (routeContextSeedRef.current === seedKey) return;

    routeContextSeedRef.current = seedKey;
    setComposerContext(cloneMessageContext(context));
  }, [role, routeTarget, selectedConversation]);

  useEffect(() => {
    if (!isProductMenuOpen) return;

    const closeProductMenu = (event: PointerEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        productMenuRef.current &&
        !productMenuRef.current.contains(target)
      ) {
        setIsProductMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeProductMenu);
    return () => document.removeEventListener("pointerdown", closeProductMenu);
  }, [isProductMenuOpen]);

  useEffect(() => {
    if (!isOrderMenuOpen) return;

    const closeOrderMenu = (event: PointerEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        orderMenuRef.current &&
        !orderMenuRef.current.contains(target)
      ) {
        setIsOrderMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOrderMenu);
    return () => document.removeEventListener("pointerdown", closeOrderMenu);
  }, [isOrderMenuOpen]);

  useEffect(() => {
    if (!showConversationDetails) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [
    activeConversationKind,
    selectedConversation?.messages.length,
    selectedCourierMessages.length,
    selectedCourierOrderId,
    selectedKey,
    showConversationDetails,
  ]);

  useEffect(() => {
    if (
      activeConversationKind !== "shop" ||
      !isOpen ||
      !showConversationDetails ||
      !selectedConversation ||
      !role ||
      !participantId
    ) {
      return;
    }
    const latestMessageId =
      getLastItem(selectedConversation.messages)?.id || "empty";
    const markKey = `${getConversationKey(
      selectedConversation,
    )}:${latestMessageId}:${role}`;
    if (markedReadRef.current.has(markKey)) return;
    markedReadRef.current.add(markKey);

    const readAt = new Date().toISOString();
    setConversations((current) =>
      current.map((conversation) =>
        getConversationKey(conversation) ===
        getConversationKey(selectedConversation)
          ? {
              ...conversation,
              unseenCount: 0,
              ...(role === "buyer"
                ? { buyerLastReadAt: readAt }
                : { sellerLastReadAt: readAt }),
            }
          : conversation,
      ),
    );
    markMessageNotificationThreadRead({
      userId: currentUser?.id || participantId,
      scope: `shop-conversations:${role}`,
      threadKey: `shop:${selectedConversation.buyerId}:${selectedConversation.vendorId}`,
      readAt,
    });
    void autopartsApi.shopConversations
      .markRead({
        buyerId: selectedConversation.buyerId,
        vendorId: selectedConversation.vendorId,
        readerId: participantId,
        readerRole: role,
      })
      .catch(() => {
        markedReadRef.current.delete(markKey);
      });
  }, [
    activeConversationKind,
    isOpen,
    participantId,
    role,
    selectedConversation,
    showConversationDetails,
  ]);

  useEffect(() => {
    if (
      activeConversationKind !== "courier" ||
      !isOpen ||
      !showConversationDetails ||
      !selectedCourierConversation ||
      role !== "seller" ||
      !currentUser?.id ||
      !sellerParticipantId
    ) {
      return;
    }

    const readAt = new Date().toISOString();
    const readOrderIds = new Set<string>();
    for (const order of selectedCourierConversation.orders) {
      const courierMessages = filterCourierSellerOrderMessages(
        order.messages,
      ).filter(
        (message) =>
          String(message.senderRole).trim().toLowerCase() === "courier",
      );
      const latestCourierMessage = getLastItem(courierMessages);
      const markKey = `courier:${order.id}:${sellerParticipantId}:${latestCourierMessage?.id || "empty"}:seller`;
      if (markedReadRef.current.has(markKey)) continue;
      markedReadRef.current.add(markKey);
      readOrderIds.add(order.id);
      markOrderMessagesRead(currentUser.id, "seller", order.id, readAt, sellerParticipantId);
      void autopartsApi.orders
        .markMessagesRead(order.id, {
          readerId: sellerParticipantId,
          readerRole: "seller",
          vendorId: sellerParticipantId,
          channel: "courier_seller",
        })
        .then((result) => {
          setSellerCourierOrders((current) =>
            current.map((entry) =>
              entry.id === result.orderId
                ? { ...entry, sellerMessagesReadAt: result.readAt }
                : entry,
            ),
          );
        })
        .catch(() => {
          markedReadRef.current.delete(markKey);
        });
    }

    if (readOrderIds.size > 0) {
      setSellerCourierOrders((current) =>
        current.map((order) =>
          readOrderIds.has(order.id)
            ? { ...order, sellerMessagesReadAt: readAt }
            : order,
        ),
      );
    }
    markMessageNotificationThreadRead({
      userId: currentUser.id,
      scope: "orders:seller-floating",
      threadKey: selectedCourierConversation.key,
      readAt,
    });
  }, [
    activeConversationKind,
    currentUser?.id,
    isOpen,
    role,
    selectedCourierConversation,
    sellerParticipantId,
    showConversationDetails,
  ]);

  const selectConversation = (conversation: ShopConversationView) => {
    setActiveConversationKind("shop");
    setSelectedKey(getConversationKey(conversation));
    setSelectedCourierOrderId("");
    setMessageDraft("");
    setComposerContext(undefined);
    setReplyToMessage(null);
    setCourierReplyToMessage(null);
    setCourierSwipeReplyOffset(null);
    setAutoReplyContextMessageId("");
    setIsProductMenuOpen(false);
    setIsOrderMenuOpen(false);
    setShowConversationDetails(true);
  };

  const selectCourierConversation = (order: OrderView) => {
    setActiveConversationKind("courier");
    setSelectedCourierOrderId(order.id);
    setSelectedKey("");
    setMessageDraft("");
    setComposerContext(undefined);
    setReplyToMessage(null);
    setCourierReplyToMessage(null);
    setCourierSwipeReplyOffset(null);
    setAutoReplyContextMessageId("");
    setIsProductMenuOpen(false);
    setIsOrderMenuOpen(false);
    setShowConversationDetails(true);
  };

  const handleSheetOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setShowConversationDetails(false);
      setActiveConversationKind("shop");
      setSelectedCourierOrderId("");
      setMessageDraft("");
      setComposerContext(undefined);
      setReplyToMessage(null);
      setCourierReplyToMessage(null);
      setCourierSwipeReplyOffset(null);
      setAutoReplyContextMessageId("");
      setIsProductMenuOpen(false);
      setIsOrderMenuOpen(false);
    }
  };

  const startCourierReply = (message: OrderMessageView) => {
    setCourierReplyToMessage(message);
  };

  const resetCourierMessageSwipe = (
    event?: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (event?.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    courierSwipeStartRef.current = null;
    setCourierSwipeReplyOffset(null);
  };

  const handleCourierMessagePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
    message: OrderMessageView,
    isOwnMessage: boolean,
  ) => {
    if (
      event.button !== 0 ||
      (event.target instanceof Element && event.target.closest("button"))
    ) {
      return;
    }

    courierSwipeStartRef.current = {
      messageId: message.id,
      pointerId: event.pointerId,
      startX: event.clientX,
      isOwnMessage,
      dragging: false,
    };
    setCourierSwipeReplyOffset({ messageId: message.id, offsetX: 0 });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleCourierMessagePointerMove = (
    event: ReactPointerEvent<HTMLDivElement>,
    message: OrderMessageView,
  ) => {
    const start = courierSwipeStartRef.current;
    if (
      !start ||
      start.messageId !== message.id ||
      start.pointerId !== event.pointerId
    ) {
      return;
    }

    const deltaX = event.clientX - start.startX;
    const directionalDelta = start.isOwnMessage
      ? Math.min(0, deltaX)
      : Math.max(0, deltaX);
    const offsetX = start.isOwnMessage
      ? Math.max(directionalDelta, -SWIPE_REPLY_MAX_OFFSET_PX)
      : Math.min(directionalDelta, SWIPE_REPLY_MAX_OFFSET_PX);

    if (
      !start.dragging &&
      Math.abs(directionalDelta) > SWIPE_DRAG_THRESHOLD_PX
    ) {
      start.dragging = true;
    }
    if (Math.abs(deltaX) > SWIPE_DRAG_THRESHOLD_PX) event.preventDefault();
    setCourierSwipeReplyOffset({ messageId: message.id, offsetX });
  };

  const handleCourierMessagePointerUp = (
    event: ReactPointerEvent<HTMLDivElement>,
    message: OrderMessageView,
  ) => {
    const start = courierSwipeStartRef.current;
    if (
      !start ||
      start.messageId !== message.id ||
      start.pointerId !== event.pointerId
    ) {
      resetCourierMessageSwipe(event);
      return;
    }

    const deltaX = event.clientX - start.startX;
    const shouldReply = start.isOwnMessage
      ? deltaX <= -SWIPE_REPLY_THRESHOLD_PX
      : deltaX >= SWIPE_REPLY_THRESHOLD_PX;

    resetCourierMessageSwipe(event);
    if (shouldReply) startCourierReply(message);
  };

  const toggleProductAttachment = (product: ProductView) => {
    setAutoReplyContextMessageId("");
    setIsOrderMenuOpen(false);
    const attachment = productToAttachment(product);
    setComposerContext((current) => {
      const currentProducts = getContextProducts(current);
      const exists = currentProducts.some(
        (entry) => entry.id === attachment.id,
      );
      const nextProducts = exists
        ? currentProducts.filter((entry) => entry.id !== attachment.id)
        : [...currentProducts, attachment].slice(0, MAX_SELECTED_PRODUCTS);

      return buildProductContext(nextProducts);
    });
  };

  const toggleOrderAttachment = (order: OrderView) => {
    setAutoReplyContextMessageId("");
    setIsProductMenuOpen(false);
    setComposerContext((current) => {
      const selectedIds = new Set(getContextOrderIds(current));
      if (selectedIds.has(order.id)) {
        selectedIds.delete(order.id);
      } else if (selectedIds.size < MAX_SELECTED_ORDERS) {
        selectedIds.add(order.id);
      }

      const selectedOrders = availableOrders.filter((candidate) =>
        selectedIds.has(candidate.id),
      );
      return buildOrdersMessageContext(
        selectedOrders,
        attachmentVendorAliases,
      );
    });
  };

  const startReply = (message: OrderMessageView) => {
    if (pendingMessageIds.has(message.id)) return;

    const repliedProductContext = isProductMessageContext(message.context)
      ? cloneMessageContext(message.context)
      : undefined;

    setReplyToMessage(message);
    setComposerContext((current) => {
      if (repliedProductContext) return repliedProductContext;
      return autoReplyContextMessageId ? undefined : current;
    });
    setAutoReplyContextMessageId(repliedProductContext ? message.id : "");
    setIsProductMenuOpen(false);
    setIsOrderMenuOpen(false);
  };

  const cancelReply = () => {
    if (replyToMessage && autoReplyContextMessageId === replyToMessage.id) {
      setComposerContext(undefined);
    }
    setReplyToMessage(null);
    setAutoReplyContextMessageId("");
  };

  const resetMessageSwipe = (event?: ReactPointerEvent<HTMLDivElement>) => {
    if (event?.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    swipeStartRef.current = null;
    setSwipeReplyOffset(null);
  };

  const handleMessagePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
    message: OrderMessageView,
    isOwnMessage: boolean,
  ) => {
    if (
      event.button !== 0 ||
      pendingMessageIds.has(message.id) ||
      (event.target instanceof Element && event.target.closest("button"))
    ) {
      return;
    }

    swipeStartRef.current = {
      messageId: message.id,
      pointerId: event.pointerId,
      startX: event.clientX,
      isOwnMessage,
      dragging: false,
    };
    setSwipeReplyOffset({ messageId: message.id, offsetX: 0 });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleMessagePointerMove = (
    event: ReactPointerEvent<HTMLDivElement>,
    message: OrderMessageView,
  ) => {
    const start = swipeStartRef.current;
    if (
      !start ||
      start.messageId !== message.id ||
      start.pointerId !== event.pointerId
    ) {
      return;
    }

    const deltaX = event.clientX - start.startX;
    const directionalDelta = start.isOwnMessage
      ? Math.min(0, deltaX)
      : Math.max(0, deltaX);
    const offsetX = start.isOwnMessage
      ? Math.max(directionalDelta, -SWIPE_REPLY_MAX_OFFSET_PX)
      : Math.min(directionalDelta, SWIPE_REPLY_MAX_OFFSET_PX);

    if (
      !start.dragging &&
      Math.abs(directionalDelta) > SWIPE_DRAG_THRESHOLD_PX
    ) {
      start.dragging = true;
    }
    if (Math.abs(deltaX) > SWIPE_DRAG_THRESHOLD_PX) {
      event.preventDefault();
    }

    setSwipeReplyOffset({ messageId: message.id, offsetX });
  };

  const handleMessagePointerUp = (
    event: ReactPointerEvent<HTMLDivElement>,
    message: OrderMessageView,
  ) => {
    const start = swipeStartRef.current;
    if (
      !start ||
      start.messageId !== message.id ||
      start.pointerId !== event.pointerId
    ) {
      resetMessageSwipe(event);
      return;
    }

    const deltaX = event.clientX - start.startX;
    const shouldReply = start.isOwnMessage
      ? deltaX <= -SWIPE_REPLY_THRESHOLD_PX
      : deltaX >= SWIPE_REPLY_THRESHOLD_PX;

    resetMessageSwipe(event);
    if (shouldReply) {
      startReply(message);
    }
  };

  const addShopMessageFiles = (incoming: File[]) => {
    setShopMessageFiles((current) => {
      const { accepted, rejected } = validateChatFiles(incoming, current);
      rejected.forEach(({ file, reason }) => toast.error(`${file.name}: ${reason}`));
      return [...current, ...accepted];
    });
  };

  const sendMessage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const message = messageDraft.trim();
    if (
      (!message && shopMessageFiles.length === 0) ||
      !selectedConversation ||
      !currentUser ||
      !role ||
      !participantId ||
      isSendingSelectedConversation ||
      shopUploadProgress !== null
    ) {
      return;
    }

    let uploadedAttachments: ConversationMediaAttachmentView[] = [];
    if (shopMessageFiles.length > 0) {
      setShopUploadProgress(0);
      try {
        for (let index = 0; index < shopMessageFiles.length; index += 1) {
          const file = shopMessageFiles[index];
          const attachment = await autopartsApi.shopConversations.uploadAttachment(
            file,
            {
              buyerId: selectedConversation.buyerId,
              vendorId: selectedConversation.vendorId,
              senderId: selectedConversation.vendorId === SUPPORT_VENDOR_ID ? currentUser.id : participantId,
              senderRole: selectedConversation.vendorId === SUPPORT_VENDOR_ID ? "buyer" : role,
            },
            (progress: ApiUploadProgress) => {
              const filePercent = progress.percent ?? 0;
              setShopUploadProgress(
                ((index + filePercent / 100) / shopMessageFiles.length) * 100,
              );
            },
          );
          uploadedAttachments.push(attachment);
        }
      } catch (error) {
        setShopUploadProgress(null);
        toast.error("Unable to upload attachment", {
          description: error instanceof Error ? error.message : "Please retry.",
        });
        return;
      }
    }

    const optimisticMessageId = `pending-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
    const messageContext = composerContext;
    const routeContextAfterSend =
      role === "buyer" &&
      routeTarget?.context &&
      selectedConversation.vendorId === routeTarget.vendorId
        ? cloneMessageContext(routeTarget.context)
        : role === "seller" &&
            messageContext &&
            isQualityAssessorConversation(selectedConversation)
          ? cloneMessageContext(messageContext)
          : undefined;
    const autoAttachedReplyContextId =
      replyToMessage?.id === autoReplyContextMessageId
        ? autoReplyContextMessageId
        : "";
    const messageReply: ConversationMessageReplyView | undefined = replyToMessage
      ? {
          messageId: replyToMessage.id,
          senderName: replyToMessage.senderName,
          message: replyToMessage.message,
        }
      : undefined;
    const optimisticMessage: OrderMessageView = {
      id: optimisticMessageId,
      senderId: selectedConversation.vendorId === SUPPORT_VENDOR_ID ? currentUser.id : participantId,
      senderRole: selectedConversation.vendorId === SUPPORT_VENDOR_ID ? "buyer" : role,
      senderName: selectedConversation.vendorId === SUPPORT_VENDOR_ID ? `${currentUser.name || "Seller"}${role === "seller" ? " · Seller" : ""}` : role === "buyer" ? currentUser.name || "Buyer" : sellerName,
      senderAvatarUrl: currentUserAvatar,
      message,
      createdAt: new Date().toISOString(),
      context: messageContext,
      replyTo: messageReply,
      attachments: uploadedAttachments,
    };
    const pending: PendingMessage = {
      buyerId: selectedConversation.buyerId,
      vendorId: selectedConversation.vendorId,
      message: optimisticMessage,
    };

    pendingMessagesRef.current.set(optimisticMessageId, pending);
    setPendingMessageIds((current) => new Set(current).add(optimisticMessageId));
    setMessageDraft("");
    setComposerContext(undefined);
    setReplyToMessage(null);
    setAutoReplyContextMessageId("");
    setIsProductMenuOpen(false);
    setIsOrderMenuOpen(false);
    setConversations((current) => {
      const currentConversation = current.find(
        (conversation) =>
          conversation.buyerId === selectedConversation.buyerId &&
          conversation.vendorId === selectedConversation.vendorId,
      );
      const conversation = currentConversation ?? selectedConversation;

      return mergeConversation(current, {
        ...conversation,
        messages: [...conversation.messages, optimisticMessage],
        unseenCount: 0,
        updatedAt: optimisticMessage.createdAt,
      });
    });

    try {
      const result = await autopartsApi.shopConversations.sendMessage({
        buyerId: selectedConversation.buyerId,
        vendorId: selectedConversation.vendorId,
        buyerName: selectedConversation.buyerName || currentUser.name || "Buyer",
        sellerName: resolveSellerName(selectedConversation.sellerName, sellerName),
        senderId: selectedConversation.vendorId === SUPPORT_VENDOR_ID ? currentUser.id : participantId,
        senderRole: selectedConversation.vendorId === SUPPORT_VENDOR_ID ? "buyer" : role,
        senderName: selectedConversation.vendorId === SUPPORT_VENDOR_ID ? `${currentUser.name || "Seller"}${role === "seller" ? " · Seller" : ""}` : role === "buyer" ? currentUser.name || "Buyer" : sellerName,
        senderAvatarUrl: currentUserAvatar,
        message,
        context: messageContext,
        replyTo: messageReply,
        attachments: uploadedAttachments,
      });
      setShopMessageFiles([]);
      setConversations((current) =>
        applyMessageResult(current, result, optimisticMessageId),
      );
      setSelectedKey(
        (current) => current || result.id || `${result.buyerId}:${result.vendorId}`,
      );
      setComposerContext((current) => current ?? routeContextAfterSend);
    } catch (error) {
      const reconciledConversation = shouldReconcileMessageFailure(error)
        ? await reconcilePendingMessage(pending)
        : null;

      if (reconciledConversation) {
        const [hydratedConversation] = await enrichConversationAvatars([
          reconciledConversation,
        ]);
        const confirmedConversation = {
          ...hydratedConversation,
          sellerName: resolveSellerName(
            hydratedConversation.sellerName,
            selectedConversation.sellerName,
          ),
        };
        setShopMessageFiles([]);
        setConversations((current) => mergeConversation(current, confirmedConversation));
        setSelectedKey(getConversationKey(confirmedConversation));
        setComposerContext((current) => current ?? routeContextAfterSend);
      } else {
        setConversations((current) =>
          current.map((conversation) =>
            getConversationKey(conversation) === getConversationKey(selectedConversation)
              ? {
                  ...conversation,
                  messages: conversation.messages.filter(
                    (entry) => entry.id !== optimisticMessageId,
                  ),
                }
              : conversation,
          ),
        );
        setMessageDraft((current) => current || message);
        setComposerContext((current) => current ?? messageContext);
        setReplyToMessage((current) => current ?? replyToMessage);
        setAutoReplyContextMessageId(autoAttachedReplyContextId);
        toast.error("Unable to send message", {
          description: error instanceof Error ? error.message : "Please try again.",
        });
      }
    } finally {
      setShopUploadProgress(null);
      pendingMessagesRef.current.delete(optimisticMessageId);
      setPendingMessageIds((current) => {
        const next = new Set(current);
        next.delete(optimisticMessageId);
        return next;
      });
    }
  };

  const sendSellerCourierMessage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const message = messageDraft.trim();
    const order = selectedCourierOrder;
    if ((!message && courierFiles.files.length === 0) || !order || !currentUser || role !== "seller" || !sellerParticipantId || isSendingCourierOrderMessage) return;
    const replyTo = courierReplyToMessage ? { messageId: courierReplyToMessage.id, senderName: courierReplyToMessage.senderName, message: courierReplyToMessage.message } : undefined;
    setIsSendingCourierOrderMessage(true);
    try {
      const attachments = await courierFiles.upload((file, onProgress) =>
        autopartsApi.orders.uploadMessageAttachment(order.id, file, "courier_seller", onProgress, sellerParticipantId));
      const created = await autopartsApi.orders.sendMessage(order.id, {
        senderId: sellerParticipantId, senderRole: "seller", senderName: sellerName,
        channel: "courier_seller", vendorId: sellerParticipantId, attachments, message, replyTo,
      });
      setSellerCourierOrders((current) => current.map((entry) => entry.id === order.id
        ? { ...entry, messages: [...(entry.messages ?? []), created], updatedAt: created.createdAt } : entry));
      setMessageDraft(""); courierFiles.clear(); setCourierReplyToMessage(null);
    } catch (error) {
      toast.error("Unable to send message", { description: getMessageSendErrorDescription(error) });
    } finally { courierFiles.setProgress(null); setIsSendingCourierOrderMessage(false); }
  };

  if (isCourierRole && currentUser) {
    return (
      <CourierOrderFloatingMessage
        currentUser={currentUser}
        pathname={pathname}
        search={search}
      />
    );
  }

  if (String(currentUser?.role || "").toLowerCase() === "admin" && pathname.startsWith("/admin") && currentUser) return <AdminFloatingSupport />;
  if (!isRolePage || !role || !currentUser) return null;

  const selectedShopName = selectedConversation
    ? role === "buyer"
      ? resolveSellerName(
          selectedConversation.sellerName,
          routeTarget?.sellerName,
        )
      : getConversationBuyerDisplayName(selectedConversation)
    : "Conversation";
  const selectedShopParticipantRoleLabel = selectedConversation
    ? role === "buyer"
      ? "Seller"
      : getConversationBuyerRoleLabel(selectedConversation)
    : "";
  const selectedName =
    activeConversationKind === "courier"
      ? selectedCourierConversation?.courierName ||
        getOrderCourierName(selectedCourierOrder)
      : selectedShopName;
  const selectedContext = composerContext;
  const selectedAvatarUrl =
    activeConversationKind === "courier"
      ? selectedCourierConversation?.courierAvatarUrl ||
        getOrderCourierAvatar(selectedCourierOrder)
      : selectedConversation
        ? role === "buyer"
          ? selectedConversation.sellerAvatarUrl || routeTarget?.sellerAvatarUrl
          : selectedConversation.buyerAvatarUrl
        : undefined;
  const hasSelectedConversation =
    activeConversationKind === "courier"
      ? Boolean(selectedCourierConversation)
      : Boolean(selectedConversation);
  const showFloatingButton = role === "buyer" || role === "seller";

  const toggleSellerMessagePushNotifications = async () => {
    if (role !== "seller") return;

    setIsUpdatingMessagePushState(true);
    try {
      const nextState =
        messagePushState === "enabled"
          ? await disablePushNotifications()
          : await enablePushNotifications();
      setMessagePushState(nextState);

      if (nextState === "enabled") {
        toast.success("SpareLynk message notifications enabled");
      } else if (nextState === "disabled") {
        toast.success("SpareLynk browser notifications disabled");
      } else if (nextState === "denied") {
        toast.error("Browser notification permission was denied");
      } else if (nextState === "unconfigured") {
        toast.error("Browser notifications are not configured on this server");
      } else if (nextState === "unavailable") {
        toast.error("Browser notifications are temporarily unavailable");
      } else {
        toast.error("Browser notifications are not supported by this browser");
      }
    } catch (error) {
      setMessagePushState("unavailable");
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to update SpareLynk notifications",
      );
    } finally {
      setIsUpdatingMessagePushState(false);
    }
  };

  const openFloatingMessages = () => {
    setIsOpen(true);

    if (role === "buyer" && routeTarget?.vendorId && currentUser) {
      const targetConversation = conversations.find(
        (conversation) => conversation.vendorId === routeTarget.vendorId,
      );
      const conversation =
        targetConversation ??
        buildEmptyBuyerConversation(currentUser, routeTarget);

      if (!targetConversation) {
        setConversations((current) => mergeConversation(current, conversation));
      }

      setActiveConversationKind("shop");
      setSelectedCourierOrderId("");
      setSelectedKey(getConversationKey(conversation));
      setComposerContext(
        routeTarget.context
          ? cloneMessageContext(routeTarget.context)
          : undefined,
      );
      routeContextSeedRef.current = routeTarget.context
        ? `${getConversationKey(conversation)}:${routeTarget.context.type}:${routeTarget.context.id}`
        : "";
      setReplyToMessage(null);
      setAutoReplyContextMessageId("");
      setIsProductMenuOpen(false);
      setIsOrderMenuOpen(false);
      setShowConversationDetails(true);
      return;
    }

    setActiveConversationKind("shop");
    setSelectedCourierOrderId("");
    setCourierReplyToMessage(null);
    setCourierSwipeReplyOffset(null);
    setShowConversationDetails(false);
  };

  return (
    <>
      {showFloatingButton ? (
        <Button
          ref={messageLauncher.ref}
          type="button"
          size="icon"
          data-dragging={messageLauncher.isDragging}
          className={FLOATING_LAUNCHER_CLASS}
          style={messageLauncher.style}
          {...messageLauncher.dragHandlers}
          aria-label={
            totalUnseen > 0
              ? `Open messages, ${totalUnseen} unseen`
              : "Open messages"
          }
          onClick={openFloatingMessages}
        >
          <MessageCircle className={FLOATING_LAUNCHER_ICON_CLASS} />
          {totalUnseen > 0 ? (
            <span className="absolute -right-1.5 -top-1.5 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground shadow-md ring-2 ring-background">
              {totalUnseen > 99 ? "99+" : totalUnseen}
            </span>
          ) : null}
        </Button>
      ) : null}

      <Sheet open={isOpen} onOpenChange={handleSheetOpenChange}>
        <SheetContent
          side="right"
          className="h-dvh max-h-dvh w-full max-w-none gap-0 overflow-hidden p-0 [&_button]:cursor-pointer sm:inset-y-3 sm:right-3 sm:h-[calc(100dvh_-_1.5rem)] sm:w-[min(560px,calc(100vw_-_1.5rem))] sm:max-w-[560px] sm:rounded-3xl sm:border"
        >
          <SheetHeader className="border-b border-border/60 bg-background/80 px-4 py-3.5 pr-14 backdrop-blur-xl sm:px-5">
            {showConversationDetails && hasSelectedConversation ? (
              <div className="flex min-w-0 items-center gap-2">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="shrink-0 cursor-pointer"
                  aria-label="Back to conversations"
                  onClick={() => setShowConversationDetails(false)}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <ParticipantAvatar
                  name={selectedName}
                  imageUrl={selectedAvatarUrl}
                  className="h-9 w-9"
                />
                <div className="min-w-0">
                  <SheetTitle className="truncate text-sm">
                    {selectedName}
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    {activeConversationKind === "courier"
                      ? "Seller and courier order conversation"
                      : "Conversation"}
                  </SheetDescription>
                  {activeConversationKind === "courier" &&
                  selectedCourierConversation ? (
                    <p className="truncate text-xs text-muted-foreground">
                      {selectedCourierConversation.orders.length === 1
                        ? `Order ${
                            selectedCourierOrder?.orderNumber ||
                            selectedCourierOrder?.id ||
                            ""
                          }`
                        : `${selectedCourierConversation.orders.length} orders`}
                    </p>
                  ) : activeConversationKind === "shop" &&
                    selectedShopParticipantRoleLabel ? (
                    <p className="truncate text-xs text-muted-foreground">
                      {selectedShopParticipantRoleLabel}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <SheetTitle className="flex items-center gap-2">
                    <MessageCircle className="h-5 w-5 text-accent" />
                    Messages
                    {totalUnseen > 0 ? (
                      <Badge variant="destructive">{totalUnseen} unseen</Badge>
                    ) : null}
                  </SheetTitle>
                  <SheetDescription className="sr-only">
                    Messages
                  </SheetDescription>
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="mr-7 shrink-0 cursor-pointer"
                  disabled={isRefreshing || isRefreshingCourierOrders}
                  aria-label="Refresh conversations"
                  onClick={() => {
                    void loadConversations({
                      refresh: true,
                      target: routeTarget,
                    });
                    if (role === "seller") {
                      void loadSellerCourierOrders({ refresh: true });
                    }
                  }}
                >
                  {isRefreshing || isRefreshingCourierOrders ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}
          </SheetHeader>

          {role === "buyer" ? <div className="flex gap-2 border-b px-4 py-2">
            <Button type="button" size="sm" variant={activeConversationKind === "shop" ? "default" : "outline"} onClick={() => { setActiveConversationKind("shop"); setShowConversationDetails(false); }}>Shops &amp; Support</Button>
            <Button type="button" size="sm" variant={activeConversationKind === "courier" ? "default" : "outline"} onClick={() => { setActiveConversationKind("courier"); setShowConversationDetails(false); }}>Courier</Button>
          </div> : null}
          {role === "seller" && !showConversationDetails ? (
            <div className="flex items-center justify-between gap-3 border-b border-border/60 bg-muted/20 px-4 py-3 sm:px-5">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-medium">
                  {messagePushState === "enabled" ? (
                    <BellRing className="h-4 w-4 text-accent" />
                  ) : (
                    <Bell className="h-4 w-4 text-muted-foreground" />
                  )}
                  SpareLynk message notifications
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Get Quality Assessment and message alerts even when SpareLynk is closed.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant={messagePushState === "enabled" ? "secondary" : "outline"}
                className="shrink-0"
                disabled={
                  isLoadingMessagePushState ||
                  isUpdatingMessagePushState ||
                  messagePushState === "unsupported" ||
                  messagePushState === "unconfigured" ||
                  messagePushState === "denied"
                }
                onClick={() => void toggleSellerMessagePushNotifications()}
              >
                {isLoadingMessagePushState || isUpdatingMessagePushState ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {messagePushState === "enabled"
                  ? "Enabled"
                  : messagePushState === "denied"
                    ? "Blocked"
                    : messagePushState === "unsupported" ||
                        messagePushState === "unconfigured"
                      ? "Unavailable"
                      : messagePushState === "unavailable"
                        ? "Retry"
                        : "Enable"}
              </Button>
            </div>
          ) : null}

          {role === "buyer" && activeConversationKind === "courier" ? (
            <OrderChannelPanel user={currentUser} role="buyer" initialOrderId={selectedCourierOrderId} />
          ) : showConversationDetails &&
          activeConversationKind === "courier" &&
          selectedCourierConversation &&
          selectedCourierOrder ? (
            <section className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-gradient-to-b from-muted/35 via-background to-background p-3 sm:p-5">
                {selectedCourierMessages.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-center">
                    <div className="max-w-xs">
                      <MessageCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                      <p className="font-medium">Start the conversation</p>
                    </div>
                  </div>
                ) : (
                  selectedCourierMessages.map((message) => {
                    const isOwnMessage =
                      String(message.senderRole).trim().toLowerCase() ===
                      "seller";
                    const senderName = isOwnMessage
                      ? sellerName
                      : message.senderName || selectedName;
                    const senderAvatarUrl =
                      message.senderAvatarUrl ||
                      (isOwnMessage ? currentUserAvatar : selectedAvatarUrl);
                    const isLatestMessage =
                      message.id === latestSelectedCourierMessage?.id;
                    const isSeenByCourier =
                      isOwnMessage &&
                      isLatestMessage &&
                      isOrderMessageSeenByRole(
                        message,
                        selectedCourierConversation.orders,
                        "courier",
                        sellerParticipantId,
                      );
                    const isMessageSwiping =
                      courierSwipeReplyOffset?.messageId === message.id;
                    const isMessageDragging =
                      isMessageSwiping &&
                      courierSwipeStartRef.current?.messageId === message.id &&
                      courierSwipeStartRef.current.dragging;
                    const messageSwipeOffset = isMessageSwiping
                      ? courierSwipeReplyOffset.offsetX
                      : 0;
                    const replyButton = (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 shrink-0 cursor-pointer text-accent opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                        aria-label={`Reply to ${senderName}`}
                        title="Reply"
                        onClick={() => startCourierReply(message)}
                      >
                        <Reply className="h-4 w-4" />
                      </Button>
                    );

                    return (
                      <div key={String(message.id)}>
                        <div
                          className={`group flex items-end gap-2 ${
                            isOwnMessage ? "justify-end" : "justify-start"
                          }`}
                        >
                        {!isOwnMessage ? (
                          <ParticipantAvatar
                            name={senderName}
                            imageUrl={senderAvatarUrl}
                            className="h-8 w-8"
                          />
                        ) : null}
                        {isOwnMessage ? replyButton : null}
                        <div
                          data-dragging={isMessageDragging ? "true" : undefined}
                          className={`min-w-0 max-w-[82%] touch-pan-y select-none sm:max-w-[72%] ${
                            isMessageSwiping
                              ? isMessageDragging
                                ? "cursor-grabbing"
                                : "cursor-grab"
                              : "cursor-grab transition-transform duration-150 ease-out"
                          }`}
                          style={{
                            transform: `translate3d(${messageSwipeOffset}px, 0, 0)`,
                          }}
                          onPointerDown={(event) =>
                            handleCourierMessagePointerDown(
                              event,
                              message,
                              isOwnMessage,
                            )
                          }
                          onPointerMove={(event) =>
                            handleCourierMessagePointerMove(event, message)
                          }
                          onPointerUp={(event) =>
                            handleCourierMessagePointerUp(event, message)
                          }
                          onPointerCancel={(event) =>
                            resetCourierMessageSwipe(event)
                          }
                        >
                          <div
                            className={`min-w-0 max-w-full rounded-2xl px-4 py-3 ${
                              isOwnMessage
                                ? "bg-accent text-accent-foreground"
                                : "border border-border bg-background"
                            }`}
                          >
                            <div className="mb-1 flex items-center justify-between gap-3">
                              <p className="min-w-0 truncate text-xs font-semibold">
                                {isOwnMessage ? "You" : senderName}
                              </p>
                              <span
                                className={`shrink-0 text-[11px] ${
                                  isOwnMessage
                                    ? "text-accent-foreground/75"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {formatConversationTime(message.createdAt)}
                              </span>
                            </div>
                            <MessageReplyPreview
                              reply={message.replyTo}
                              context={message.context}
                              ownMessage={isOwnMessage}
                            />
                            {!message.replyTo ? (
                              <ConversationMessageContext
                                context={message.context}
                                compact
                              />
                            ) : null}
                            <p className="whitespace-pre-wrap wrap-break-word text-sm">
                              {message.message}
                            </p>
                            {message.attachments?.map((attachment) => <ShopMessageAttachment key={attachment.id} attachment={attachment} />)}
                          </div>
                        </div>
                        {!isOwnMessage ? replyButton : null}
                        {isOwnMessage ? (
                          <ParticipantAvatar
                            name={senderName}
                            imageUrl={senderAvatarUrl}
                            className="h-8 w-8"
                          />
                        ) : null}
                        </div>
                        {isSeenByCourier ? (
                          <SeenByAvatar
                            name={selectedName}
                            imageUrl={selectedAvatarUrl}
                          />
                        ) : null}
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <form
                className="space-y-2 border-t border-border/60 bg-background/90 p-3 backdrop-blur-xl sm:p-4"
                onSubmit={sendSellerCourierMessage}
              >
                {courierReplyToMessage ? (
                  <div className="relative rounded-lg border border-border bg-muted/40 p-3 pr-10">
                    <div className="flex items-start gap-2">
                      <Reply className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-semibold">
                          Replying to{" "}
                          {courierReplyToMessage.senderName || "message"}
                        </p>
                        <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground">
                          {courierReplyToMessage.message}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="absolute right-1.5 top-1.5 h-7 w-7 cursor-pointer"
                      aria-label="Cancel reply"
                      onClick={() => setCourierReplyToMessage(null)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : null}
                <ConversationMessageContext context={selectedCourierOrder ? buildOrderMessageContext(selectedCourierOrder, sellerParticipantId) : undefined} compact />
                <ChatAttachmentPicker files={courierFiles.files} progress={courierFiles.progress} disabled={isSendingCourierOrderMessage} onFiles={courierFiles.addFiles} onRemove={courierFiles.removeAt} />
                <div className="flex items-end gap-2">
                  <Textarea
                    value={messageDraft}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                      setMessageDraft(event.target.value)
                    }
                    placeholder={`Message ${selectedName}...`}
                    rows={2}
                    maxLength={ORDER_MESSAGE_DRAFT_MAX_LENGTH}
                    disabled={isSendingCourierOrderMessage}
                    className="min-h-11 resize-none rounded-xl border-border/70 bg-muted/25 px-3 py-2.5 shadow-none focus-visible:bg-background"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    className="h-11 w-11 shrink-0 cursor-pointer rounded-xl shadow-sm"
                    disabled={
                      (!messageDraft.trim() && courierFiles.files.length === 0) ||
                      !selectedCourierOrder ||
                      isSendingCourierOrderMessage
                    }
                    aria-label="Send message"
                  >
                    {isSendingCourierOrderMessage ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </form>
            </section>
          ) : showConversationDetails && selectedConversation ? (
            <section className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-gradient-to-b from-muted/35 via-background to-background p-3 sm:p-5">
                {selectedConversation.messages.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-center">
                    <div className="max-w-xs">
                      <MessageCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                      <p className="font-medium">Start the conversation</p>
                    </div>
                  </div>
                ) : (
                  selectedConversation.messages.map((message) => {
                    const senderRole = normalizeRole(message.senderRole);
                    const isOwnMessage = senderRole === role;
                    const senderName =
                      message.senderName ||
                      (isOwnMessage ? currentUser.name : selectedName) ||
                      "User";
                    const senderAvatarUrl =
                      message.senderAvatarUrl ||
                      (["buyer", "quality_assessor"].includes(senderRole)
                        ? selectedConversation.buyerAvatarUrl
                        : selectedConversation.sellerAvatarUrl) ||
                      (isOwnMessage ? currentUserAvatar : selectedAvatarUrl);
                    const isLatestMessage =
                      message.id === latestSelectedShopMessage?.id;
                    const isSeenByParticipant =
                      isOwnMessage &&
                      isLatestMessage &&
                      !pendingMessageIds.has(message.id) &&
                      isMessageReadAtOrAfter(
                        message,
                        role === "buyer"
                          ? selectedConversation.sellerLastReadAt
                          : selectedConversation.buyerLastReadAt,
                      );
                    const messageSwipeOffset =
                      swipeReplyOffset?.messageId === message.id
                        ? swipeReplyOffset.offsetX
                        : 0;
                    const isMessageSwiping =
                      swipeReplyOffset?.messageId === message.id;
                    const isMessageDragging =
                      isMessageSwiping &&
                      swipeStartRef.current?.messageId === message.id &&
                      swipeStartRef.current.dragging;
                    const isSwipeReady =
                      isMessageSwiping &&
                      Math.abs(messageSwipeOffset) >= SWIPE_REPLY_THRESHOLD_PX;
                    const replyButton = (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className={`h-8 w-8 shrink-0 cursor-pointer rounded-full border bg-background text-accent shadow-sm transition-[transform,background-color,color,border-color] hover:border-accent/60 hover:bg-accent/10 hover:text-accent ${
                          isSwipeReady
                            ? "scale-110 border-accent bg-accent text-accent-foreground"
                            : "border-accent/30"
                        }`}
                        disabled={pendingMessageIds.has(message.id)}
                        aria-label={`Reply to ${senderName}`}
                        title={
                          isOwnMessage
                            ? "Reply or swipe left"
                            : "Reply or swipe right"
                        }
                        onClick={() => startReply(message)}
                      >
                        <Reply className="h-4 w-4" />
                      </Button>
                    );

                    return (
                      <div key={String(message.id)}>
                        <div
                          className={`group flex items-end gap-2 ${
                            isOwnMessage ? "justify-end" : "justify-start"
                          }`}
                        >
                        {!isOwnMessage ? (
                          <ParticipantAvatar
                            name={senderName}
                            imageUrl={senderAvatarUrl}
                            className="h-8 w-8"
                          />
                        ) : null}
                        {isOwnMessage ? replyButton : null}
                        <div
                          data-dragging={isMessageDragging ? "true" : undefined}
                          className={`min-w-0 max-w-[82%] touch-pan-y select-none sm:max-w-[72%] ${
                            isMessageSwiping
                              ? isMessageDragging
                                ? "cursor-grabbing"
                                : "cursor-grab"
                              : "cursor-grab transition-transform duration-150 ease-out"
                          }`}
                          style={{
                            transform: `translate3d(${messageSwipeOffset}px, 0, 0)`,
                          }}
                          onPointerDown={(
                            event: ReactPointerEvent<HTMLDivElement>,
                          ) =>
                            handleMessagePointerDown(
                              event,
                              message,
                              isOwnMessage,
                            )
                          }
                          onPointerMove={(
                            event: ReactPointerEvent<HTMLDivElement>,
                          ) => handleMessagePointerMove(event, message)}
                          onPointerUp={(
                            event: ReactPointerEvent<HTMLDivElement>,
                          ) => handleMessagePointerUp(event, message)}
                          onPointerCancel={(
                            event: ReactPointerEvent<HTMLDivElement>,
                          ) => resetMessageSwipe(event)}
                        >
                          <div
                            className={`min-w-0 max-w-full rounded-2xl px-4 py-3 ${
                              isOwnMessage
                                ? "bg-accent text-accent-foreground"
                                : "border border-border bg-background"
                            }`}
                          >
                            <div className="mb-1 flex items-center justify-between gap-3">
                              <p className="min-w-0 truncate text-xs font-semibold">
                                {senderName}
                              </p>
                              <span
                                className={`shrink-0 text-[11px] ${
                                  isOwnMessage
                                    ? "text-accent-foreground/75"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {pendingMessageIds.has(message.id)
                                  ? "Sending..."
                                  : formatConversationTime(message.createdAt)}
                              </span>
                            </div>
                            <MessageReplyPreview
                              reply={message.replyTo}
                              context={message.context}
                              ownMessage={isOwnMessage}
                            />
                            {!message.replyTo ? (
                              <ConversationMessageContext
                                context={message.context}
                              />
                            ) : null}
                            {message.message ? (
                              <p className="whitespace-pre-wrap wrap-break-word text-sm">
                                {message.message}
                              </p>
                            ) : null}
                            {(message.attachments ?? []).map((attachment) => (
                              <ShopMessageAttachment
                                key={attachment.id}
                                attachment={attachment}
                              />
                            ))}
                          </div>
                        </div>
                        {!isOwnMessage ? replyButton : null}
                        {isOwnMessage ? (
                          <ParticipantAvatar
                            name={senderName}
                            imageUrl={senderAvatarUrl}
                            className="h-8 w-8"
                          />
                        ) : null}
                        </div>
                        {isSeenByParticipant ? (
                          <SeenByAvatar
                            name={selectedName}
                            imageUrl={selectedAvatarUrl}
                          />
                        ) : null}
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <form
                className="space-y-2 border-t border-border/60 bg-background/90 p-3 backdrop-blur-xl sm:p-4"
                onSubmit={sendMessage}
              >
                {replyToMessage ? (
                  <div className="relative rounded-lg border border-border bg-muted/40 p-3 pr-10">
                    <div className="flex items-start gap-2">
                      <Reply className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-semibold">
                          Replying to {replyToMessage.senderName || "message"}
                        </p>
                        {isProductMessageContext(replyToMessage.context) ? (
                          <div className="mt-2 [&>div]:mb-0">
                            <ConversationMessageContext
                              context={replyToMessage.context}
                              compact
                            />
                          </div>
                        ) : null}
                        <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground">
                          {replyToMessage.message}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="absolute right-1.5 top-1.5 h-7 w-7 cursor-pointer"
                      aria-label="Cancel reply"
                      onClick={cancelReply}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}

                {selectedContext &&
                autoReplyContextMessageId !== replyToMessage?.id ? (
                  <div className="relative rounded-lg border border-border bg-muted/30 p-2 pr-10 [&>div]:mb-0">
                    <ConversationMessageContext
                      context={selectedContext}
                      compact
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="secondary"
                      className="absolute right-1.5 top-1.5 h-7 w-7 cursor-pointer rounded-full"
                      aria-label="Remove attached product or context"
                      onClick={() => {
                        setComposerContext(undefined);
                        setAutoReplyContextMessageId("");
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}

                {role === "buyer" ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <div ref={productMenuRef} className="relative">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-9 cursor-pointer gap-2"
                        disabled={isSendingSelectedConversation}
                        aria-expanded={isProductMenuOpen}
                        onClick={() => {
                          setIsOrderMenuOpen(false);
                          setIsProductMenuOpen((current) => !current);
                        }}
                      >
                        <PackagePlus className="h-4 w-4" />
                        {selectedProducts.length > 0
                          ? `${selectedProducts.length} product${
                              selectedProducts.length === 1 ? "" : "s"
                            } attached`
                          : "Attach products"}
                        <ChevronDown
                          className={`h-3.5 w-3.5 transition-transform duration-200 ${
                            isProductMenuOpen ? "rotate-180" : ""
                          }`}
                        />
                      </Button>

                      {isProductMenuOpen ? (
                        <div className="absolute bottom-full left-0 z-layer-popover mb-2 w-[min(22rem,calc(100vw_-_2rem))] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl">
                          <div className="border-b border-border px-3 py-2">
                            <p className="text-xs font-semibold">
                              Select products from {selectedName}
                            </p>
                          </div>
                          <div className="max-h-64 overflow-y-auto p-1.5">
                            {isLoadingProducts &&
                            availableProducts.length === 0 ? (
                              <div className="flex items-center justify-center px-3 py-6 text-xs text-muted-foreground">
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Loading products...
                              </div>
                            ) : availableProducts.length === 0 ? (
                              <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                                No products are available for this seller.
                              </p>
                            ) : (
                              availableProducts.map((product) => {
                                const isSelected = selectedProductIds.has(
                                  product.id,
                                );
                                const productImage = getProductImage(product);
                                return (
                                  <Button variant="ghost"
                                    key={String(product.id)}
                                    type="button"
                                    className="h-auto w-full justify-start gap-2 whitespace-normal rounded-lg px-2 py-2 text-left"
                                    onClick={() =>
                                      toggleProductAttachment(product)
                                    }
                                  >
                                    {productImage ? (
                                      <img
                                        src={productImage}
                                        alt={product.name || product.title}
                                        loading="lazy"
                                        decoding="async"
                                        className="h-10 w-10 shrink-0 rounded-md border border-border object-cover"
                                      />
                                    ) : (
                                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
                                        <PackagePlus className="h-4 w-4 text-muted-foreground" />
                                      </div>
                                    )}
                                    <span className="min-w-0 flex-1 truncate text-xs font-medium">
                                      {product.name || product.title}
                                    </span>
                                    <span
                                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                                        isSelected
                                          ? "border-accent bg-accent text-accent-foreground"
                                          : "border-border"
                                      }`}
                                      aria-hidden="true"
                                    >
                                      {isSelected ? (
                                        <Check className="h-3.5 w-3.5" />
                                      ) : null}
                                    </span>
                                  </Button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div ref={orderMenuRef} className="relative">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-9 cursor-pointer gap-2"
                        disabled={isSendingSelectedConversation}
                        aria-expanded={isOrderMenuOpen}
                        onClick={() => {
                          setIsProductMenuOpen(false);
                          setIsOrderMenuOpen((current) => !current);
                        }}
                      >
                        <PackageCheck className="h-4 w-4" />
                        {selectedOrderContextIds.length > 0
                          ? `${selectedOrderContextIds.length} order${
                              selectedOrderContextIds.length === 1 ? "" : "s"
                            } attached`
                          : "Attach orders"}
                        <ChevronDown
                          className={`h-3.5 w-3.5 transition-transform duration-200 ${
                            isOrderMenuOpen ? "rotate-180" : ""
                          }`}
                        />
                      </Button>

                      {isOrderMenuOpen ? (
                        <div className="absolute bottom-full left-0 z-layer-popover mb-2 w-[min(24rem,calc(100vw_-_2rem))] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl">
                          <div className="border-b border-border px-3 py-2">
                            <p className="text-xs font-semibold">Attach orders</p>
                            <p className="text-[11px] text-muted-foreground">
                              Select up to {MAX_SELECTED_ORDERS} orders from
                              this seller.
                            </p>
                          </div>
                          <div className="max-h-64 overflow-y-auto p-1.5">
                            {isLoadingOrders &&
                            availableOrders.length === 0 ? (
                              <div className="flex items-center justify-center px-3 py-6">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              </div>
                            ) : availableOrders.length === 0 ? (
                              <div className="flex flex-col items-center justify-center gap-2 px-3 py-6 text-center text-xs text-muted-foreground">
                                <PackageCheck className="h-5 w-5" />
                                <p>No orders from this seller are available.</p>
                              </div>
                            ) : (
                              availableOrders.map((order) => {
                                const context = buildOrderMessageContext(
                                  order,
                                  attachmentVendorAliases,
                                );
                                const firstImage = context.images[0];
                                const isSelected =
                                  selectedOrderContextIdSet.has(order.id);
                                return (
                                  <Button variant="ghost"
                                    key={String(order.id)}
                                    type="button"
                                    className="h-auto w-full justify-start gap-2 whitespace-normal rounded-lg px-2 py-2 text-left"
                                    onClick={() => toggleOrderAttachment(order)}
                                  >
                                    {firstImage ? (
                                      <img
                                        src={firstImage}
                                        alt={context.label}
                                        loading="lazy"
                                        decoding="async"
                                        className="h-10 w-10 shrink-0 rounded-md border border-border object-cover"
                                      />
                                    ) : (
                                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
                                        <PackageCheck className="h-4 w-4 text-muted-foreground" />
                                      </div>
                                    )}
                                    <span className="min-w-0 flex-1">
                                      <span className="block truncate text-xs font-medium">
                                        {context.label}
                                      </span>
                                      <span className="block truncate text-[11px] text-muted-foreground">
                                        {context.products
                                          ?.map((product) => product.name)
                                          .join(", ")}
                                      </span>
                                    </span>
                                    <span
                                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                                        isSelected
                                          ? "border-accent bg-accent text-accent-foreground"
                                          : "border-border"
                                      }`}
                                      aria-hidden="true"
                                    >
                                      {isSelected ? (
                                        <Check className="h-3.5 w-3.5" />
                                      ) : null}
                                    </span>
                                  </Button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    {selectedProducts.length > 0 ||
                    selectedOrderContextIds.length > 0 ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="ml-auto h-9 cursor-pointer text-xs text-muted-foreground"
                        onClick={() => {
                          setComposerContext(undefined);
                          setAutoReplyContextMessageId("");
                        }}
                      >
                        Clear attachment
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                <div className="flex items-end gap-2">
                  <ChatAttachmentPicker
                    files={shopMessageFiles}
                    progress={shopUploadProgress}
                    disabled={isSendingSelectedConversation || shopUploadProgress !== null}
                    onFiles={addShopMessageFiles}
                    onRemove={(index) => setShopMessageFiles((current) => current.filter((_, i) => i !== index))}
                  />
                  <Textarea
                    value={messageDraft}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                      setMessageDraft(event.target.value)
                    }
                    placeholder={`Message ${selectedName}...`}
                    rows={2}
                    maxLength={2000}
                    disabled={
                      isSendingSelectedConversation || shopUploadProgress !== null
                    }
                    className="min-h-11 resize-none rounded-xl border-border/70 bg-muted/25 px-3 py-2.5 shadow-none focus-visible:bg-background"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    className="h-11 w-11 shrink-0 cursor-pointer rounded-xl shadow-sm"
                    disabled={
                      (!messageDraft.trim() && shopMessageFiles.length === 0) ||
                      isSendingSelectedConversation ||
                      shopUploadProgress !== null
                    }
                    aria-label="Send message"
                  >
                    {shopUploadProgress !== null ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </form>
            </section>
          ) : (
            <section className="flex min-h-0 flex-1 flex-col">
              <div className="border-b border-border p-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setSearchQuery(event.target.value)
                    }
                    placeholder={`Search ${
                      role === "buyer" ? "shops" : "buyers, assessors or couriers"
                    }...`}
                    className="h-11 rounded-xl border-border/70 bg-muted/30 pl-9 shadow-none focus-visible:bg-background"
                  />
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto">
                {(loadError || courierLoadError) &&
                conversations.length === 0 &&
                sellerCourierConversations.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                    <MessageCircle className="mb-3 h-9 w-9 text-muted-foreground" />
                    <p className="text-sm font-semibold">
                      Unable to load conversations
                    </p>
                    <p className="mt-1 max-w-sm text-xs text-muted-foreground">
                      {loadError || courierLoadError}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-4 cursor-pointer"
                      disabled={isRefreshing || isRefreshingCourierOrders}
                      onClick={() => {
                        void loadConversations({
                          refresh: true,
                          target: routeTarget,
                        });
                        if (role === "seller") {
                          void loadSellerCourierOrders({ refresh: true });
                        }
                      }}
                    >
                      {isRefreshing || isRefreshingCourierOrders ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 h-4 w-4" />
                      )}
                      Retry
                    </Button>
                  </div>
                ) : (isLoading || isLoadingCourierOrders) &&
                  conversations.length === 0 &&
                  sellerCourierConversations.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading conversations...
                  </div>
                ) : filteredConversations.length === 0 &&
                  filteredSellerCourierConversations.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center px-5 text-center">
                    <MessageCircle className="mb-2 h-8 w-8 text-muted-foreground" />
                    <p className="text-sm font-medium">
                      No conversations found
                    </p>
                  </div>
                ) : (
                  <>
                    {filteredConversations.map((conversation) => {
                      const key = getConversationKey(conversation);
                      const lastMessage = getLastItem(conversation.messages);
                      const name =
                        role === "buyer"
                          ? resolveSellerName(
                              conversation.sellerName,
                              routeTarget?.sellerName,
                            )
                          : getConversationBuyerDisplayName(conversation);
                      const avatarUrl =
                        role === "buyer"
                          ? conversation.sellerAvatarUrl ||
                            (conversation.vendorId === routeTarget?.vendorId
                              ? routeTarget.sellerAvatarUrl
                              : undefined)
                          : conversation.buyerAvatarUrl;
                      return (
                        <Button variant="ghost"
                          key={String(key)}
                          type="button"
                          className="mx-2 my-1 h-auto w-[calc(100%_-_1rem)] justify-start whitespace-normal rounded-2xl border border-transparent px-3 py-3 text-left transition hover:border-border/70 hover:bg-muted/70"
                          onClick={() => selectConversation(conversation)}
                        >
                          <div className="flex items-start gap-3">
                            <ParticipantAvatar
                              name={name}
                              imageUrl={avatarUrl}
                              className="h-10 w-10"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold">
                                    {name}
                                  </p>
                                  {role === "seller" &&
                                  isQualityAssessorConversation(conversation) ? (
                                    <p className="truncate text-[11px] font-medium text-accent">
                                      Quality Assessor
                                    </p>
                                  ) : null}
                                </div>
                                <span className="shrink-0 text-[11px] text-muted-foreground">
                                  {formatConversationTime(
                                    lastMessage?.createdAt ||
                                      conversation.updatedAt,
                                  )}
                                </span>
                              </div>
                              <div className="mt-1 flex items-center gap-2">
                                <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                                  {lastMessage?.message ||
                                    "Start a conversation"}
                                </p>
                                {conversation.unseenCount > 0 ? (
                                  <Badge className="h-5 min-w-5 justify-center rounded-full px-1.5 text-[10px]">
                                    {conversation.unseenCount > 99
                                      ? "99+"
                                      : conversation.unseenCount}
                                  </Badge>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </Button>
                      );
                    })}

                    {role === "seller"
                      ? filteredSellerCourierConversations.map(
                          ({
                            key,
                            orders,
                            order,
                            courierName,
                            courierAvatarUrl,
                            lastMessage,
                            unseenCount,
                          }) => (
                            <Button variant="ghost"
                              key={String(key)}
                              type="button"
                              className="mx-2 my-1 h-auto w-[calc(100%_-_1rem)] justify-start whitespace-normal rounded-2xl border border-transparent px-3 py-3 text-left transition hover:border-border/70 hover:bg-muted/70"
                              onClick={() => selectCourierConversation(order)}
                            >
                              <div className="flex items-start gap-3">
                                <ParticipantAvatar
                                  name={courierName}
                                  imageUrl={courierAvatarUrl}
                                  className="h-10 w-10"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex min-w-0 items-center gap-2">
                                      <p className="truncate text-sm font-semibold">
                                        {courierName}
                                      </p>
                                      <Badge
                                        variant="outline"
                                        className="shrink-0 text-[10px]"
                                      >
                                        Courier
                                      </Badge>
                                    </div>
                                    <span className="shrink-0 text-[11px] text-muted-foreground">
                                      {formatConversationTime(
                                        lastMessage?.createdAt ||
                                          order.updatedAt,
                                      )}
                                    </span>
                                  </div>
                                  <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                                    {orders.length === 1
                                      ? `Order ${order.orderNumber || order.id}`
                                      : `${orders.length} orders`}
                                  </p>
                                  <div className="mt-1 flex items-center gap-2">
                                    <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                                      {lastMessage?.message ||
                                        "Start a conversation"}
                                    </p>
                                    {unseenCount > 0 ? (
                                      <Badge className="h-5 min-w-5 justify-center rounded-full px-1.5 text-[10px]">
                                        {unseenCount > 99 ? "99+" : unseenCount}
                                      </Badge>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </Button>
                          ),
                        )
                      : null}
                  </>
                )}
              </div>
            </section>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
